import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./auth-transition.js";
import type { DragEvent as ReactDragEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, TouchEvent } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import { voiceFailureMessage } from "./voice";
import {
  AlarmClock,
  Archive,
  Binoculars,
  Calendar,
  CalendarClock,
  Clock3,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  FileText,
  GripVertical,
  House,
  LogOut,
  Origami,
  Palette,
  Plus,
  RefreshCw,
  Search,
  Settings as SettingsIcon,
  Star,
  ListTodo,
  AudioLines,
  LoaderCircle,
  Mic,
  MicOff,
  MapPin,
  Camera,
  Download,
  ExternalLink,
  ImagePlus,
  Maximize2,
  Square,
  Trash2,
  X,
} from "lucide-react";
import * as api from "./api";
import { ReleaseStatus } from "./ReleaseStatus";
import { EntryIcon, IconPicker, suggestEntryIcon } from "./entryIcons";
import {
  cacheEntries,
  beginOfflineSession,
  resumeOfflineSession,
  offlineSessionRevision,
  assertOfflineRevision,
  captureOfflineSession,
  assertOfflineSession,
  subscribeOfflineLogout,
  OfflineSessionEndedError,
  clearOfflineData,
  clearOfflineOperations,
  queueOfflineOperation,
  readCachedEntries,
  readOfflineOperations,
} from "./offline";
import { clearAppBadge, needsIosHomeScreenInstall, supportsWebPush, syncAppBadge } from "./notifications";
import type { Attachment, Entry, EntryLocation, Page, Priority, Session, VoiceCapture, VoiceProposal } from "./types";

type EntryIntent = "note" | "task" | "neutral";
type EditorTarget =
  | { mode: "new"; intent: EntryIntent }
  | { mode: "existing"; uuid: string };
type EditorWindow = EditorTarget & { windowId: string };
type FlowBucket = "today" | "upcoming" | "later" | "completed";
type EntryContextMenu = { uuid: string; x: number; y: number };
type PhotoPreview = {
  key: string;
  filename: string;
  previewUrl: string;
  file?: File;
  attachment?: Attachment;
  uploading?: boolean;
};
const isImageFile = (file: Pick<File, "type" | "name">) =>
  file.type.startsWith("image/") || /\.(heic|heif)$/i.test(file.name);
const nav: Array<{ page: Page; label: string; icon: typeof House }> = [
  { page: "flow", label: "Übersicht", icon: House },
  { page: "notes", label: "Notizen", icon: FileText },
  { page: "tasks", label: "Aufgaben", icon: ListTodo },
  { page: "calendar", label: "Kalender", icon: Calendar },
  { page: "search", label: "Suchen", icon: Search },
  { page: "archive", label: "Archiv", icon: Archive },
  { page: "settings", label: "Einstellungen", icon: SettingsIcon },
];
const formatDate = (value: Date) =>
  new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(value);
const formatTime = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Berlin",
      }).format(new Date(value))
    : "";
function localDate(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}
function dateOffset(days: number) {
  const value = new Date();
  value.setHours(12, 0, 0, 0);
  value.setDate(value.getDate() + days);
  return localDate(value);
}
const dueFromDate = (value: string, time = "09:00") =>
  value ? new Date(`${value}T${time || "09:00"}:00`).toISOString() : null;
type LocationCapture = NonNullable<api.EntryCreatePayload["location"]>;
const coordinateLabel = (location: Pick<LocationCapture, "latitude" | "longitude">) =>
  `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`;
const locationAddress = (location: LocationCapture | EntryLocation | null) => {
  if (!location || !("address" in location)) return null;
  const resolved = location as EntryLocation;
  return resolved.address || [resolved.street, resolved.house_number, resolved.postal_code, resolved.city, resolved.country]
    .filter(Boolean)
    .join(", ") || null;
};
const locationSummary = (location: NonNullable<LocationCapture | Entry["location"]>) =>
  locationAddress(location) || coordinateLabel(location);
function captureLocation(): Promise<LocationCapture> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("unsupported"));
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { coords } = position;
        resolve({
          latitude: coords.latitude, longitude: coords.longitude, accuracy_meters: coords.accuracy,
          altitude_meters: coords.altitude, altitude_accuracy_meters: coords.altitudeAccuracy,
          heading_degrees: coords.heading, speed_meters_per_second: coords.speed,
          captured_at: new Date(position.timestamp).toISOString(),
        });
      },
      () => reject(new Error("unavailable")),
      { enableHighAccuracy: true, timeout: 7000, maximumAge: 15000 },
    );
  });
}
const dateInput = (value?: string | null) =>
  value ? localDate(new Date(value)) : "";
const timeInput = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Berlin",
      }).format(new Date(value))
    : "09:00";
const intentForPage = (page: Page): EntryIntent =>
  page === "notes"
    ? "note"
    : page === "tasks" || page === "calendar"
      ? "task"
      : "neutral";
const taskPriority = (priority: Priority) =>
  priority === "low" || priority === "medium" || priority === "high" ? priority : "medium";
function flowBucket(entry: Entry): FlowBucket | "note" | "undated" {
  if (entry.completed_at || entry.state === "completed") return "completed";
  if (!entry.due_at && !entry.due_on) return entry.is_todo ? "undated" : "note";
  const due = entry.due_on || dateInput(entry.due_at);
  if (due <= dateOffset(0)) return "today";
  return due <= dateOffset(7) ? "upcoming" : "later";
}
function openBucket(entry: Entry): Exclude<FlowBucket, "completed"> {
  const bucket = flowBucket(entry);
  return bucket === "today" || bucket === "upcoming" || bucket === "later"
    ? bucket
    : "today";
}
function dueLabel(value?: string | null) {
  if (!value) return "Ohne Datum";
  const date = dateInput(value);
  if (date === dateOffset(0)) return `Heute, ${formatTime(value)}`;
  if (date === dateOffset(1)) return `Morgen, ${formatTime(value)}`;
  const label = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));
  return `${label}, ${formatTime(value)}`;
}

async function discardEndedSession(action: () => Promise<void>): Promise<void> {
  try { await action(); }
  catch (error) { if (!(error instanceof OfflineSessionEndedError)) throw error; }
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null),
    [authPending, setAuthPending] = useState(true),
    [entries, setEntries] = useState<Entry[]>([]),
    [editorWindows, setEditorWindows] = useState<EditorWindow[]>([]),
    [page, setPage] = useState<Page>("flow"),
    [mobileColumn, setMobileColumn] = useState<FlowBucket>("today"),
    [mobileSelectionMode, setMobileSelectionMode] = useState(false),
    [mobileCompletedOpen, setMobileCompletedOpen] = useState(false),
    [query, setQuery] = useState(""),
    [selectedEntryUuids, setSelectedEntryUuids] = useState<Set<string>>(new Set()),
    [entryContextMenu, setEntryContextMenu] = useState<EntryContextMenu | null>(null),
    [completionPending, setCompletionPending] = useState(false),
    [toast, setToast] = useState<string | null>(null),
    [updateRegistration, setUpdateRegistration] = useState<ServiceWorkerRegistration | null>(null),
    [pullDistance, setPullDistance] = useState(0),
    [refreshing, setRefreshing] = useState(false),
    [voiceOpen, setVoiceOpen] = useState(false),
    [voiceStream, setVoiceStream] = useState<MediaStream | null>(null),
    [voiceStartError, setVoiceStartError] = useState(""),
    [online, setOnline] = useState(navigator.onLine);
  const [, setCursor] = useState(0),
    searchInput = useRef<HTMLInputElement>(null),
    pullStart = useRef<number | null>(null),
    voiceRequestSequence = useRef(0);
  const notify = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2800);
  }, []);
  const closeVoice = useCallback(() => {
    voiceRequestSequence.current += 1;
    setVoiceOpen(false);
    setVoiceStartError("");
    setVoiceStream((current) => {
      current?.getTracks().forEach((track) => track.stop());
      return null;
    });
  }, []);
  const startVoice = useCallback(async () => {
    if (!online) return;
    const requestSequence = ++voiceRequestSequence.current;
    setVoiceStream(null);
    setVoiceStartError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceOpen(true);
      setVoiceStartError("Dieser Browser unterstützt keine Audioaufnahme.");
      return;
    }
    // Die Berechtigungsanfrage muss direkt aus dem Fingertipp kommen. Das Panel
    // wird anschließend nur mit dem bereits freigegebenen Stream verbunden.
    const streamRequest = navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    setVoiceOpen(true);
    try {
      const stream = await streamRequest;
      if (voiceRequestSequence.current !== requestSequence) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      setVoiceStream(stream);
    } catch {
      if (voiceRequestSequence.current === requestSequence) {
        setVoiceStartError("Bitte erlauben Sie MemoHub den Mikrofonzugriff.");
      }
    }
  }, [online]);
  const openEditor = useCallback((target: EditorTarget) => {
    setEditorWindows((windows) => {
      if (target.mode === "existing") {
        const existing = windows.findIndex(
          (window) => window.mode === "existing" && window.uuid === target.uuid,
        );
        if (existing >= 0) {
          const selected = windows[existing];
          return [...windows.filter((_, index) => index !== existing), selected];
        }
      }
      return [
        ...windows,
        {
          ...target,
          windowId:
            target.mode === "existing"
              ? `entry:${target.uuid}`
              : `draft:${crypto.randomUUID()}`,
        },
      ];
    });
  }, []);
  const closeEditor = useCallback((windowId: string) => {
    setEditorWindows((windows) =>
      windows.filter((window) => window.windowId !== windowId),
    );
  }, []);
  const activateEditor = useCallback((windowId: string) => {
    setEditorWindows((windows) => {
      const selected = windows.find((window) => window.windowId === windowId);
      if (!selected || windows.at(-1)?.windowId === windowId) return windows;
      return [...windows.filter((window) => window.windowId !== windowId), selected];
    });
  }, []);
  const load = useCallback(
    async (subject: string) => discardEndedSession(async () => {
      const lease = captureOfflineSession(subject);
      try {
        const result = await api.listEntries();
        setEntries(result.items);
        setCursor(result.cursor);
        await cacheEntries(subject, result.items);
        const pending = await readOfflineOperations(subject);
        if (pending.length) {
          const synced = await api.syncOperations(result.cursor, pending),
            merged = mergeEntries(result.items, [
              ...synced.changes,
              ...synced.operations.flatMap(
                (item) =>
                  [item.entry, item.conflict_copy].filter(Boolean) as Entry[],
              ),
            ]);
          setEntries(merged);
          setCursor(synced.cursor);
          await cacheEntries(subject, merged);
          await clearOfflineOperations(subject);
          notify("Offline-Änderungen synchronisiert");
        }
      } catch (error) {
        if (error instanceof OfflineSessionEndedError) return;
        assertOfflineSession(lease);
        const cached = await readCachedEntries(subject);
        assertOfflineSession(lease);
        setEntries(cached);
        if (cached.length) notify("Offline-Arbeitsstand geöffnet");
      }
    }),
    [notify],
  );
  useEffect(() => {
    const updateOnline = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateOnline);
    window.addEventListener("offline", updateOnline);
    return () => {
      window.removeEventListener("online", updateOnline);
      window.removeEventListener("offline", updateOnline);
    };
  }, []);
  useEffect(() => subscribeOfflineLogout(() => {
    setSession(null);
    setAuthPending(false);
    setEntries([]);
    setEditorWindows([]);
    setSelectedEntryUuids(new Set());
    setEntryContextMenu(null);
    setQuery("");
    setToast(null);
    setCursor(0);
    closeVoice();
    void clearAppBadge().catch(() => {});
  }), [closeVoice]);
  useEffect(() => {
    let cancelled = false;
    let revision = offlineSessionRevision();
    const code = new URLSearchParams(location.hash.slice(1)).get("idhub_code");
    if (window.turnusAuthTransition.enter({appName: "MemoHub", returned: Boolean(code), navigate: routeToMemoHub})) {
      setAuthPending(false);
      return () => { cancelled = true; };
    }
    const handoff = code
        ? fetch("/api/v2/auth/handoff", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
          }).then(() => history.replaceState(null, "", location.pathname + location.search))
        : Promise.resolve();
    handoff
      .then(api.getSession)
      .then(async (value) => {
        if (cancelled) return;
        assertOfflineRevision(revision);
        await beginOfflineSession(value.subject_uuid, revision);
        revision = offlineSessionRevision();
        if (cancelled) return;
        localStorage.setItem("memohub_subject", value.subject_uuid);
        localStorage.setItem("memohub_display_name", value.display_name || "MemoHub");
        setSession(value);
        return load(value.subject_uuid);
      })
      .catch(async (error) => {
        if (cancelled || error instanceof OfflineSessionEndedError || offlineSessionRevision() !== revision) return;
        const subject = localStorage.getItem("memohub_subject");
        if (!navigator.onLine && subject) {
          try {
            await resumeOfflineSession(subject, revision);
            const cached = await readCachedEntries(subject);
            if (cancelled) return;
            assertOfflineRevision(revision);
            setSession({ subject_uuid: subject, display_name: localStorage.getItem("memohub_display_name") || "MemoHub" });
            setEntries(cached);
            notify("MemoHub ist offline verfügbar");
          } catch { if (!cancelled) setSession(null); }
        } else {
          setSession(null);
          if (error?.status !== 401) notify("Sitzung konnte nicht geprüft werden.");
          if (error?.status === 401) await clearOfflineData().catch(() => {});
        }
      })
      .finally(() => { if (!cancelled) setAuthPending(false); });
    return () => { cancelled = true; };
  }, [load, notify]);
  useEffect(() => {
    if (session) void syncAppBadge(entries);
  }, [entries, session]);
  useEffect(() => {
    if (!session || !entries.length) return;
    const params = new URLSearchParams(location.search), linkedEntry = params.get("entry");
    if (!linkedEntry || !entries.some((entry) => entry.uuid === linkedEntry)) return;
    openEditor({ mode: "existing", uuid: linkedEntry });
    params.delete("entry");
    history.replaceState(null, "", `${location.pathname}${params.size ? `?${params}` : ""}${location.hash}`);
  }, [entries, openEditor, session]);
  useEffect(() => {
    const update = (event: Event) => {
      const registration = (event as CustomEvent<ServiceWorkerRegistration>).detail;
      if (registration?.waiting) setUpdateRegistration(registration);
    };
    window.addEventListener("memohub-update", update);
    return () => window.removeEventListener("memohub-update", update);
  }, []);
  const applyAvailableUpdate = useCallback(() => {
    const worker = updateRegistration?.waiting;
    if (!worker || !("serviceWorker" in navigator)) {
      window.location.reload();
      return;
    }
    const reload = () => {
      navigator.serviceWorker.removeEventListener("controllerchange", reload);
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", reload);
    worker.postMessage("SKIP_WAITING");
  }, [updateRegistration]);
  useEffect(() => {
    const keys = (event: KeyboardEvent) => {
      if (event.metaKey && event.key.toLowerCase() === "n") {
        event.preventDefault();
        openEditor({ mode: "new", intent: intentForPage(page) });
      }
      if (event.metaKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchInput.current?.focus();
      }
    };
    window.addEventListener("keydown", keys);
    return () => window.removeEventListener("keydown", keys);
  }, [openEditor, page]);
  useEffect(() => {
    const close = (event: Event) => {
      if (event instanceof MouseEvent && event.type === "click" && event.button !== 0) return;
      setEntryContextMenu(null);
    };
    if (!entryContextMenu) return;
    window.addEventListener("click", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [entryContextMenu]);
  useEffect(() => {
    const clear = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !editorWindows.length && selectedEntryUuids.size) {
        setSelectedEntryUuids(new Set());
        setEntryContextMenu(null);
      }
    };
    window.addEventListener("keydown", clear);
    return () => window.removeEventListener("keydown", clear);
  }, [editorWindows.length, selectedEntryUuids.size]);
  useEffect(() => {
    setSelectedEntryUuids(new Set());
    setEntryContextMenu(null);
  }, [query]);
  useEffect(() => {
    document.body.classList.toggle("modal-open", editorWindows.length > 0);
    return () => document.body.classList.remove("modal-open");
  }, [editorWindows.length]);
  function navigate(target: Page) {
    if (target === "search") {
      searchInput.current?.focus();
      return;
    }
    setSelectedEntryUuids(new Set());
    setEntryContextMenu(null);
    setPage(target);
  }
  function openEntry(uuid: string) {
    setSelectedEntryUuids(new Set());
    setEntryContextMenu(null);
    openEditor({ mode: "existing", uuid });
  }
  function toggleEntrySelection(uuid: string) {
    setSelectedEntryUuids((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
    setEntryContextMenu(null);
  }
  function setMobileSelection(active: boolean) {
    setMobileSelectionMode(active);
    if (!active) setSelectedEntryUuids(new Set());
  }
  function showEntryContextMenu(uuid: string, x: number, y: number) {
    if (!selectedEntryUuids.has(uuid)) setSelectedEntryUuids(new Set([uuid]));
    setEntryContextMenu({ uuid, x, y });
  }
  async function runBulkAction(action: "archive" | "trash") {
    return discardEndedSession(async () => {
      const selected = entries.filter((entry) => selectedEntryUuids.has(entry.uuid));
      if (!selected.length) return;
      setEntryContextMenu(null);
      try {
        const result = await api.bulkEntryAction(selected, action);
        setEntries((items) => mergeEntries(items, result.items));
      } catch (error) {
        if (error instanceof OfflineSessionEndedError) return;
        if (!session) throw error;
        const status = (error as Error & { status?: number }).status;
        if (navigator.onLine && status && status < 500) {
          if (status === 409) {
            await load(session.subject_uuid);
            notify("Mindestens ein Eintrag wurde zwischenzeitlich geändert. Auswahl aktualisiert.");
          } else notify("Sammelaktion ist für diese Auswahl nicht zulässig.");
          return;
        }
        const now = new Date().toISOString();
        const changed = selected.map((entry) => ({
          ...entry,
          archived_at: action === "archive" ? now : entry.archived_at,
          trashed_at: action === "trash" ? now : entry.trashed_at,
          version: entry.version + 1,
          updated_at: now,
        }));
        const next = mergeEntries(entries, changed);
        setEntries(next);
        await cacheEntries(session.subject_uuid, next);
        await Promise.all(selected.map((entry) => queueOfflineOperation(session.subject_uuid, {
          operation_id: crypto.randomUUID(),
          kind: action,
          entry_uuid: entry.uuid,
          base_version: entry.version,
          payload: {},
        })));
      }
      setSelectedEntryUuids(new Set());
      notify(`${selected.length} ${selected.length === 1 ? "Eintrag" : "Einträge"} ${action === "archive" ? "archiviert" : "in den Papierkorb verschoben"}`);
    });
  }
  async function runEntryAction(entry: Entry, action: "archive" | "trash") {
    return discardEndedSession(async () => {
      try {
        const updated = action === "archive" ? await api.archiveEntry(entry) : await api.trashEntry(entry);
        setEntries((items) => items.map((item) => item.uuid === updated.uuid ? updated : item));
      } catch (error) {
        if (error instanceof OfflineSessionEndedError) return;
        if (!session) return;
        const now = new Date().toISOString();
        const updated: Entry = {
          ...entry,
          archived_at: action === "archive" ? now : entry.archived_at,
          trashed_at: action === "trash" ? now : entry.trashed_at,
          version: entry.version + 1,
          updated_at: now,
        };
        const next = entries.map((item) => item.uuid === updated.uuid ? updated : item);
        setEntries(next);
        await cacheEntries(session.subject_uuid, next);
        await queueOfflineOperation(session.subject_uuid, {
          operation_id: crypto.randomUUID(),
          kind: action,
          entry_uuid: entry.uuid,
          base_version: entry.version,
          payload: {},
        });
      }
      notify(action === "archive" ? "Archiviert" : "In den Papierkorb verschoben");
    });
  }
  function beginPull(event: TouchEvent<HTMLElement>) {
    if (editorWindows.length || (document.scrollingElement?.scrollTop || 0) > 0) return;
    pullStart.current = event.touches[0]?.clientY ?? null;
  }
  function movePull(event: TouchEvent<HTMLElement>) {
    if (pullStart.current === null || refreshing) return;
    const distance =
      (event.touches[0]?.clientY || pullStart.current) - pullStart.current;
    if (distance > 0) setPullDistance(Math.min(78, distance * 0.48));
  }
  async function finishPull() {
    pullStart.current = null;
    if (pullDistance < 52 || refreshing || !session) {
      setPullDistance(0);
      return;
    }
    setRefreshing(true);
    try {
      await load(session.subject_uuid);
      notify("Aktualisiert");
    } finally {
      setRefreshing(false);
      setPullDistance(0);
    }
  }
  async function create(payload: api.EntryCreatePayload): Promise<Entry> {
    try {
      const { entry, titleGenerationUnavailable } = await api.createEntry(payload);
      setEntries((items) => [entry, ...items]);
      notify(titleGenerationUnavailable
        ? "Eintrag angelegt – automatische Titelerzeugung über OpenAI war nicht verfügbar."
        : "Eintrag angelegt");
      return entry;
    } catch (error) {
      if (error instanceof OfflineSessionEndedError) throw error;
      if (typeof error === "object" && error !== null && "status" in error) throw error;
      if (!session) throw error;
      const uuid = crypto.randomUUID(),
        now = new Date().toISOString(),
        entry = {
          uuid,
          owner_subject_uuid: session.subject_uuid,
          kind: payload.due_at ? "task" : "note",
          title: payload.title,
          markdown: payload.markdown,
          tags: payload.tags,
          priority: payload.priority || "none",
          favorite: payload.favorite,
          icon_key: payload.icon_key || null,
          state: payload.state,
          focus_date: payload.focus_date || null,
          due_at: payload.due_at || null,
          completed_at: null,
          sort_order: Date.now(),
          archived_at: null,
          trashed_at: null,
          version: 1,
          updated_at: now,
          recurrence: payload.recurrence || null,
        } satisfies Entry;
      const next = [entry, ...entries];
      setEntries(next);
      await cacheEntries(session.subject_uuid, next);
      await queueOfflineOperation(session.subject_uuid, {
        operation_id: crypto.randomUUID(),
        kind: "create",
        entry_uuid: uuid,
        payload: { ...payload },
      });
      notify("Offline gespeichert");
      return entry;
    }
  }
  async function save(entry: Entry, changes: Partial<Entry>, requireServer = false): Promise<Entry> {
    try {
      const updated = await api.patchEntry(entry, changes);
      setEntries((items) =>
        items.map((item) => (item.uuid === updated.uuid ? updated : item)),
      );
      notify("Gespeichert");
      return updated;
    } catch (error) {
      if (requireServer) throw error;
      if (error instanceof OfflineSessionEndedError) throw error;
      if (!session) return entry;
      const updated = {
          ...entry,
          ...changes,
          updated_at: new Date().toISOString(),
        },
        next = entries.map((item) =>
          item.uuid === entry.uuid ? updated : item,
        );
      setEntries(next);
      await cacheEntries(session.subject_uuid, next);
      await queueOfflineOperation(session.subject_uuid, {
        operation_id: crypto.randomUUID(),
        kind: "patch",
        entry_uuid: entry.uuid,
        base_version: entry.version,
        payload: changes,
      });
      notify("Offline gespeichert");
      return updated;
    }
  }
  async function setCompletion(entry: Entry, completed: boolean): Promise<Entry> {
    const updated = await (completed ? api.completeEntry(entry) : api.reopenEntry(entry));
    setEntries((items) => items.map((item) => item.uuid === updated.uuid ? updated : item));
    notify(completed ? "Erledigt" : "Wieder geöffnet");
    return updated;
  }
  async function move(entry: Entry, bucket: FlowBucket) {
    return discardEndedSession(async () => {
      if (flowBucket(entry) === bucket) return;
      if (entry.is_todo && (bucket === "completed" || flowBucket(entry) === "completed")) {
        try { await setCompletion(entry, bucket === "completed"); }
        catch (error) { notify((error as Error).message); }
        return;
      }
      try {
        let current = entry;
        if (bucket === "completed") current = await api.completeEntry(current);
        else {
          if (flowBucket(current) === "completed")
            current = await api.reopenEntry(current);
          const dueAt =
            bucket === "today"
              ? dueFromDate(dateOffset(0))
              : bucket === "upcoming"
                ? current.due_at &&
                  dateInput(current.due_at) > dateOffset(0) &&
                  dateInput(current.due_at) <= dateOffset(7)
                  ? current.due_at
                  : dueFromDate(dateOffset(1))
                : current.due_at && dateInput(current.due_at) > dateOffset(7)
                  ? current.due_at
                  : dueFromDate(dateOffset(8));
          current = await api.patchEntry(current, {
            ...(current.is_todo && !current.due_at ? { due_on: dateInput(dueAt) } : {}),
            due_at: current.is_todo && !current.due_at ? null : dueAt,
            focus_date: dueAt ? dateInput(dueAt) : null,
            priority:
              dueAt && current.priority === "none" ? "medium" : current.priority,
          });
        }
        setEntries((items) =>
          items.map((item) => (item.uuid === current.uuid ? current : item)),
        );
        notify(
          bucket === "completed"
            ? "Erledigt"
            : bucket === "today"
              ? "Für heute eingeplant"
              : bucket === "upcoming"
                ? "Für demnächst eingeplant"
                : "Nach Später verschoben",
        );
      } catch (error) {
        if (error instanceof OfflineSessionEndedError) return;
        if (!session) return;
        const dueAt =
            bucket === "today"
              ? dueFromDate(dateOffset(0))
              : bucket === "upcoming"
                ? entry.due_at &&
                  dateInput(entry.due_at) > dateOffset(0) &&
                  dateInput(entry.due_at) <= dateOffset(7)
                  ? entry.due_at
                  : dueFromDate(dateOffset(1))
                : entry.due_at && dateInput(entry.due_at) > dateOffset(7)
                  ? entry.due_at
                  : dueFromDate(dateOffset(8)),
          updated: Entry = {
            ...entry,
            kind: "task",
            state:
              bucket === "completed"
                ? "completed"
                : bucket === "today"
                  ? "today"
                  : "inbox",
            ...(entry.is_todo && !entry.due_at ? { due_on: dateInput(dueAt) } : {}),
            due_at: entry.is_todo && !entry.due_at ? null : dueAt,
            focus_date: dueAt ? dateInput(dueAt) : null,
            priority:
              dueAt && entry.priority === "none" ? "medium" : entry.priority,
            completed_at:
              bucket === "completed" ? new Date().toISOString() : null,
          },
          next = entries.map((item) =>
            item.uuid === entry.uuid ? updated : item,
          );
        setEntries(next);
        await cacheEntries(session.subject_uuid, next);
        await queueOfflineOperation(session.subject_uuid, {
          operation_id: crypto.randomUUID(),
          kind: bucket === "completed" ? "complete" : "patch",
          entry_uuid: entry.uuid,
          base_version: entry.version,
          payload:
            bucket === "completed"
              ? {}
              : {
                  ...(entry.is_todo && !entry.due_at ? { due_on: updated.due_on } : {}),
                  due_at: updated.due_at,
                  focus_date: updated.focus_date,
                  priority: updated.priority,
                },
        });
        notify("Offline verschoben");
      }
    });
  }
  async function logout() {
    await window.turnusAuthTransition.logout({
      appName: "MemoHub",
      revoke: () => api.logout(),
      clear: () => {
        void clearOfflineData().catch(() => notify("Der lokale Speicher konnte nicht vollständig geleert werden."));
        void clearAppBadge();
      },
      navigate: routeToMemoHub,
      onError: () => notify("Abmeldung am Server konnte nicht bestätigt werden."),
    });
  }
  const visibleEntries = useMemo(() => {
    let result = entries.filter(
      (item) => !item.archived_at && !item.trashed_at,
    );
    if (page === "notes") result = result.filter((item) => item.kind === "note");
    if (page === "tasks")
      result = result
        .filter((item) => item.kind === "task")
        .sort((a, b) => (a.due_on || a.due_at || "9999").localeCompare(b.due_on || b.due_at || "9999"));
    if (page === "archive")
      result = entries.filter((item) => item.archived_at && !item.trashed_at);
    const normalized = query.trim().toLowerCase();
    if (normalized)
      result = result.filter((item) =>
        `${item.title} ${item.markdown} ${item.tags.join(" ")}`
          .toLowerCase()
          .includes(normalized),
      );
    return result;
  }, [entries, page, query]);
  if (authPending)
    return <AuthPending />;
  if (!session) return <>{toast && <div className="toast" role="status">{toast}</div>}<Login /></>;
  return (
    <div className="app-shell">
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
      {updateRegistration?.waiting && (
        <div className="app-update-layer" role="presentation">
          <section className="app-update-dialog" role="alertdialog" aria-modal="true" aria-label="Neue MemoHub-Version verfügbar">
            <strong>Neue Version verfügbar</strong>
            <p>MemoHub wurde verbessert. Zum Aktualisieren wird diese App einmal neu geladen.</p>
            {editorWindows.length > 0 && <small>Offene, noch nicht gespeicherte Änderungen gehen beim Aktualisieren verloren.</small>}
            <footer>
              <button type="button" onClick={() => setUpdateRegistration(null)}>Später</button>
              <button type="button" className="app-update-confirm" onClick={applyAvailableUpdate}>Jetzt aktualisieren</button>
            </footer>
          </section>
        </div>
      )}
      <div
        className={`pull-indicator ${refreshing ? "refreshing" : ""}`}
        aria-hidden="true"
        style={{
          transform: `translate(-50%, ${pullDistance - 44}px)`,
          opacity: pullDistance ? 1 : 0,
        }}
      >
        <RefreshCw size={14} />
        {refreshing
          ? "Aktualisiert …"
          : pullDistance >= 52
            ? "Loslassen"
            : "Zum Aktualisieren ziehen"}
      </div>
      <Sidebar
        page={page}
        session={session}
        navigate={navigate}
        logout={logout}
      />
      <main
        className="workspace"
        onClick={(event) => {
          if (!selectedEntryUuids.size) return;
          const target = event.target as HTMLElement;
          if (!target.closest(".entry-card, .selection-toolbar, .entry-context-menu, button, input, textarea, select")) {
            setSelectedEntryUuids(new Set());
            setEntryContextMenu(null);
          }
        }}
        onTouchStart={beginPull}
        onTouchMove={movePull}
        onTouchEnd={() => void finishPull()}
        onTouchCancel={() => {
          pullStart.current = null;
          setPullDistance(0);
        }}
      >
        <header className="topbar">
          <div>
            <h1>{nav.find((item) => item.page === page)?.label}</h1>
            <p>{formatDate(new Date())}</p>
          </div>
          <div
            className="search-field"
            title="Aktuelle Ansicht durchsuchen (⌘ K)"
          >
            <Search size={18} />
            <input
              ref={searchInput}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Notizen und Aufgaben durchsuchen"
              aria-label="Notizen und Aufgaben durchsuchen"
            />
            {query ? (
              <button
                type="button"
                className="search-clear"
                aria-label="Suche löschen"
                title="Suche löschen"
                onClick={() => {
                  setQuery("");
                  searchInput.current?.focus();
                }}
              >
                <X size={15} />
              </button>
            ) : (
              <kbd>⌘ K</kbd>
            )}
          </div>
          <div className="top-actions">
            <button
              className="voice-launch voice-desktop-button"
              aria-label="Diktat starten"
              disabled={!online}
              onClick={() => void startVoice()}
              title={online ? "Notiz oder Aufgabe diktieren" : "Diktat benötigt eine Internetverbindung"}
            >
              {online ? <AudioLines size={17} /> : <MicOff size={17} />}
            </button>
            <div className="create-control">
              <button
                className="primary new-entry-button"
                aria-label="Neu"
                onClick={() =>
                  openEditor({ mode: "new", intent: intentForPage(page) })
                }
                title="Neuen Eintrag anlegen (⌘ N)"
              >
                <Plus size={20} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>
        {selectedEntryUuids.size > 0 && (
          <SelectionToolbar
            count={selectedEntryUuids.size}
            canArchive={entries.some(
              (entry) => selectedEntryUuids.has(entry.uuid) && !entry.archived_at,
            )}
            onArchive={() => void runBulkAction("archive")}
            onTrash={() => void runBulkAction("trash")}
            onClear={() => setSelectedEntryUuids(new Set())}
          />
        )}
        {page === "flow" ? (
          <Flow
            entries={visibleEntries}
            mobileColumn={mobileColumn}
            setMobileColumn={setMobileColumn}
            mobileSelectionMode={mobileSelectionMode}
            setMobileSelectionMode={setMobileSelection}
            completedOpen={mobileCompletedOpen}
            setCompletedOpen={setMobileCompletedOpen}
            onSelect={openEntry}
            selectedEntryUuids={selectedEntryUuids}
            onToggleSelection={toggleEntrySelection}
            onContextMenu={showEntryContextMenu}
            onMove={move}
            onArchive={(entry) => void runEntryAction(entry, "archive")}
            onTrash={(entry) => void runEntryAction(entry, "trash")}
            onOpenCalendar={() => setPage("calendar")}
          />
        ) : page === "calendar" ? (
          <CalendarView
            entries={visibleEntries}
            onOpen={openEntry}
            onMove={move}
            onArchive={(entry) => void runEntryAction(entry, "archive")}
            onTrash={(entry) => void runEntryAction(entry, "trash")}
          />
        ) : page === "settings" ? (
          <>
            <section className="session-actions" aria-label="Sitzung">
              <button type="button" onClick={() => void logout()}>Abmelden</button>
            </section>
            <Settings onToast={notify} canReload={editorWindows.length === 0 && !voiceOpen} onReload={applyAvailableUpdate} />
          </>
        ) : (
          <Collection
            title={nav.find((item) => item.page === page)?.label || "MemoHub"}
            entries={visibleEntries}
            onSelect={openEntry}
            mobileSelectionMode={mobileSelectionMode}
            setMobileSelectionMode={setMobileSelection}
            selectedEntryUuids={selectedEntryUuids}
            onToggleSelection={toggleEntrySelection}
            onContextMenu={showEntryContextMenu}
            onMove={move}
            onArchive={(entry) => void runEntryAction(entry, "archive")}
            onTrash={(entry) => void runEntryAction(entry, "trash")}
          />
        )}
      </main>
      {(["flow", "notes", "tasks", "calendar"] as Page[]).includes(page) && (
        <MobileCompletedStrip
          entries={entries.filter(
            (entry) => entry.kind === "task" && flowBucket(entry) === "completed",
          )}
          open={mobileCompletedOpen}
          setOpen={setMobileCompletedOpen}
          onSelect={openEntry}
          selectedEntryUuids={selectedEntryUuids}
          mobileSelectionMode={mobileSelectionMode}
          onToggleSelection={toggleEntrySelection}
          onContextMenu={showEntryContextMenu}
          onMove={move}
          onArchive={(entry) => void runEntryAction(entry, "archive")}
          onTrash={(entry) => void runEntryAction(entry, "trash")}
        />
      )}
      {entryContextMenu && (
        <EntryContextActions
          position={entryContextMenu}
          entry={entries.find((entry) => entry.uuid === entryContextMenu.uuid)}
          count={selectedEntryUuids.size}
          onOpen={() => openEntry(entryContextMenu.uuid)}
          completionPending={completionPending}
          onToggleCompletion={async () => {
            const entry = entries.find((item) => item.uuid === entryContextMenu.uuid);
            if (!entry || completionPending) return;
            setCompletionPending(true);
            try {
              await setCompletion(entry, flowBucket(entry) !== "completed");
              setEntryContextMenu(null);
              setSelectedEntryUuids(new Set());
            } catch (error) {
              notify((error as Error).message);
            } finally {
              setCompletionPending(false);
            }
          }}
          onArchive={() => void runBulkAction("archive")}
          onTrash={() => void runBulkAction("trash")}
        />
      )}
      {editorWindows.length > 0 && (
        <button
          className="editor-backdrop"
          aria-label="Dialog schließen"
          onClick={() =>
            window.dispatchEvent(new Event("memohub-editor-dismiss"))
          }
        />
      )}{" "}
      {editorWindows.map((target, index) => (
        <Editor
          key={target.windowId}
          entry={
            target.mode === "existing"
              ? entries.find((item) => item.uuid === target.uuid)
              : undefined
          }
          onClose={() => closeEditor(target.windowId)}
          onActivate={() => activateEditor(target.windowId)}
          topmost={index === editorWindows.length - 1}
          windowIndex={index}
          onCreate={create}
          onSave={save}
          onSetCompletion={setCompletion}
          onSchedule={async (entry, durationMinutes) => {
            const linked = await api.scheduleEntry(entry.uuid, durationMinutes);
            setEntries((items) => items.map((item) => item.uuid === linked.uuid ? linked : item));
            return linked;
          }}
          onPhotoCountChange={(entryUuid, imageAttachmentCount) => setEntries((items) =>
            items.map((item) => item.uuid === entryUuid ? { ...item, image_attachment_count: imageAttachmentCount } : item),
          )}
          onToast={notify}
          intent={target.mode === "new" ? target.intent : "neutral"}
          onArchive={
            target.mode === "existing"
              ? async () => {
                  const entry = entries.find((item) => item.uuid === target.uuid);
                  if (!entry) return;
                  const updated = await api.archiveEntry(entry);
                  setEntries((items) =>
                    items.map((item) =>
                      item.uuid === updated.uuid ? updated : item,
                    ),
                  );
                  closeEditor(target.windowId);
                  notify("Archiviert");
                }
              : undefined
          }
        />
      ))}
      {voiceOpen && (
        <VoiceCapturePanel
          online={online}
          mediaStream={voiceStream}
          startError={voiceStartError}
          onClose={closeVoice}
          onCreated={(entry) => {
            setEntries((items) => [entry, ...items]);
            closeVoice();
            notify(entry.kind === "task" ? "Aufgabe aus Diktat angelegt" : "Notiz aus Diktat angelegt");
          }}
          onToast={notify}
        />
      )}
      <nav className="bottom-nav">
        {nav
          .filter((item) =>
            ["flow", "notes", "tasks", "calendar", "settings"].includes(
              item.page,
            ),
          )
          .map(({ page: key, label, icon: Icon }) => (
            <button
              className={page === key ? "active" : ""}
              aria-label={key === "settings" ? "Einstellungen" : undefined}
              title={
                key === "search"
                  ? "Aktuelle Ansicht durchsuchen"
                  : `${label} öffnen`
              }
              onClick={() => navigate(key)}
              key={key}
            >
              <Icon size={21} />
              <span>{key === "settings" ? "Mehr" : label}</span>
            </button>
          ))}
      </nav>
    </div>
  );
}

function VoiceCapturePanel({
  online,
  mediaStream,
  startError,
  onClose,
  onCreated,
  onToast,
}: {
  online: boolean;
  mediaStream: MediaStream | null;
  startError: string;
  onClose: () => void;
  onCreated: (entry: Entry) => void;
  onToast: (message: string) => void;
}) {
  const [phase, setPhase] = useState<"arming" | "recording" | "processing" | "ready" | "error">("arming"),
    [seconds, setSeconds] = useState(0),
    [capture, setCapture] = useState<VoiceCapture | null>(null),
    [proposal, setProposal] = useState<VoiceProposal | null>(null),
    [error, setError] = useState(""),
    [location, setLocation] = useState<LocationCapture | null>(null),
    [position, setPosition] = useState({ x: 0, y: 0 }),
    [autoSaveSeconds, setAutoSaveSeconds] = useState<number | null>(null);
  const recorder = useRef<MediaRecorder | null>(null),
    stream = useRef<MediaStream | null>(null),
    chunks = useRef<Blob[]>([]),
    clock = useRef<number | null>(null),
    limit = useRef<number | null>(null),
    dragStart = useRef<{ pointerId: number; clientX: number; clientY: number; x: number; y: number } | null>(null),
    autoSaveStarted = useRef(false),
    autoSavePaused = useRef(false),
    applying = useRef(false);

  const stopTracks = useCallback(() => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    if (clock.current !== null) window.clearInterval(clock.current);
    if (limit.current !== null) window.clearTimeout(limit.current);
    clock.current = null;
    limit.current = null;
  }, []);

  const pollCapture = useCallback(async (uuid: string) => {
    for (let attempt = 0; attempt < 90; attempt += 1) {
      const current = await api.getVoiceCapture(uuid);
      setCapture(current);
      if (current.status === "ready" || current.status === "needs_input") {
        if (!current.proposal) throw new Error("Der Diktatvorschlag ist unvollständig.");
        setProposal({
          ...current.proposal,
          priority: current.proposal.due_at ? taskPriority(current.proposal.priority) : "none",
        });
        setPhase("ready");
        return;
      }
      if (current.status === "failed") {
        throw new Error(voiceFailureMessage(current.error_code));
      }
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
    }
    throw new Error("Die Diktatauswertung dauert zu lange. Bitte versuchen Sie es erneut.");
  }, []);

  const uploadRecording = useCallback(async (blob: Blob) => {
    setPhase("processing");
    try {
      const created = await api.createVoiceCapture(blob);
      setCapture(created);
      await pollCapture(created.uuid);
    } catch (caught) {
      setError((caught as Error).message);
      setPhase("error");
    }
  }, [pollCapture]);

  const beginRecording = useCallback((media: MediaStream) => {
    if (!online || recorder.current?.state === "recording") return;
    const candidates = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"],
      mimeType = candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) || "";
    stream.current = media;
    chunks.current = [];
    const active = new MediaRecorder(media, mimeType ? { mimeType } : undefined);
    recorder.current = active;
    active.ondataavailable = (event) => {
      if (event.data.size) chunks.current.push(event.data);
    };
    active.onstop = () => {
      const blob = new Blob(chunks.current, { type: active.mimeType || mimeType || "audio/webm" });
      stopTracks();
      if (blob.size) void uploadRecording(blob);
      else {
        setError("Die Aufnahme war leer.");
        setPhase("error");
      }
    };
    active.start(400);
    setSeconds(0);
    setError("");
    setPhase("recording");
    clock.current = window.setInterval(() => setSeconds((value) => value + 1), 1000);
    limit.current = window.setTimeout(() => active.state === "recording" && active.stop(), 90_000);
  }, [online, stopTracks, uploadRecording]);

  useEffect(() => {
    return stopTracks;
  }, [stopTracks]);

  useEffect(() => {
    if (mediaStream && phase === "arming") beginRecording(mediaStream);
  }, [beginRecording, mediaStream, phase]);

  useEffect(() => {
    if (!startError) return;
    setError(startError);
    setPhase("error");
  }, [startError]);

  useEffect(() => {
    let active = true;
    void captureLocation().then((value) => active && setLocation(value), () => undefined);
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      void close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const canAutoSave = Boolean(
    proposal
    && proposal.intent === "task"
    && proposal.due_at
    && proposal.title.trim()
    && proposal.markdown.trim(),
  );

  useEffect(() => {
    if (phase !== "ready" || !proposal || autoSaveStarted.current) return;
    autoSaveStarted.current = true;
    if (canAutoSave) {
      autoSavePaused.current = false;
      setAutoSaveSeconds(7);
    }
  }, [canAutoSave, phase, proposal]);

  useEffect(() => {
    if (autoSaveSeconds === null || !canAutoSave || phase !== "ready" || autoSavePaused.current) return;
    if (autoSaveSeconds === 0) {
      void apply();
      return;
    }
    const timeout = window.setTimeout(() => {
      if (!autoSavePaused.current) setAutoSaveSeconds((seconds) => seconds === null ? null : seconds - 1);
    }, 1000);
    return () => window.clearTimeout(timeout);
  }, [autoSaveSeconds, canAutoSave, phase]);

  function pauseAutoSave(event?: ReactPointerEvent<HTMLElement>) {
    if (autoSaveSeconds === null || autoSavePaused.current) return;
    autoSavePaused.current = true;
    if ((event?.target as HTMLElement | null)?.closest(".voice-apply")) return;
    window.setTimeout(() => setAutoSaveSeconds(null), 0);
  }

  function startDrag(event: ReactPointerEvent<HTMLElement>) {
    if (window.innerWidth <= 720 || (event.target as HTMLElement).closest("button, input, label, select, textarea, summary")) return;
    dragStart.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      x: position.x,
      y: position.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function moveDrag(event: ReactPointerEvent<HTMLElement>) {
    const start = dragStart.current;
    if (!start || start.pointerId !== event.pointerId) return;
    setPosition({ x: start.x + event.clientX - start.clientX, y: start.y + event.clientY - start.clientY });
  }
  function stopDrag(event: ReactPointerEvent<HTMLElement>) {
    if (dragStart.current?.pointerId === event.pointerId) dragStart.current = null;
  }

  async function close() {
    if (recorder.current?.state === "recording") {
      recorder.current.onstop = null;
      recorder.current.stop();
    }
    stopTracks();
    if (capture && capture.status !== "applied") {
      try { await api.discardVoiceCapture(capture.uuid); } catch { /* Ablaufbereinigung erfolgt serverseitig. */ }
    }
    onClose();
  }

  function stopRecording() {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }

  async function apply() {
    if (!capture || !proposal || applying.current) return;
    if (proposal.intent === "task" && !proposal.due_at) {
      pauseAutoSave();
      onToast("Bitte legen Sie zuerst eine Fälligkeit fest");
      return;
    }
    applying.current = true;
    pauseAutoSave();
    try {
      onCreated(await api.applyVoiceCapture(capture, proposal, location));
    } catch (caught) {
      setError((caught as Error).message);
      setPhase("error");
    } finally {
      applying.current = false;
    }
  }

  const dueDate = dateInput(proposal?.due_at),
    dueTime = timeInput(proposal?.due_at);
  function setProposalDue(dateValue: string, timeValue = dueTime) {
    if (!proposal) return;
    setProposal({
      ...proposal,
      intent: dateValue ? "task" : "note",
      due_at: dateValue ? dueFromDate(dateValue, timeValue) : null,
      priority: dateValue ? taskPriority(proposal.priority) : "none",
      needs_follow_up: false,
      follow_up_question: null,
    });
  }

  return (
    <div className="voice-layer" role="presentation">
      <button className="voice-backdrop" aria-label="Diktat schließen" onClick={() => void close()} />
      <section className="voice-panel" role="dialog" aria-modal="true" aria-label="MemoHub-Diktat" style={{ transform: `translate(${position.x}px, ${position.y}px)` }} onPointerDownCapture={pauseAutoSave}>
        <header className="voice-drag-handle" onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={stopDrag} onPointerCancel={stopDrag}>
          <span className={`voice-state-icon ${phase}`}>
            {phase === "recording" ? <AudioLines size={21} /> : phase === "processing" ? <LoaderCircle size={21} /> : <Mic size={21} />}
          </span>
          <div>
            <strong>Diktat</strong>
            <small>
              {phase === "recording" ? "Ich höre zu …" : phase === "processing" ? "Transkription und Einordnung laufen …" : phase === "ready" ? "Vorschlag prüfen" : "Notiz oder Aufgabe sprechen"}
            </small>
          </div>
          <button className="voice-close" aria-label="Abbrechen" title="Diktat verwerfen" onClick={() => void close()}><X size={18} /></button>
        </header>

        {phase === "recording" && (
          <div className="voice-recording">
            <div className="voice-wave" aria-hidden="true">{Array.from({ length: 13 }, (_, index) => <i key={index} />)}</div>
            <strong>{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</strong>
            <p>Sprechen Sie natürlich: „Aufgabe: Morgen um 14 Uhr Angebot senden“ oder „Notiz: Idee für …“</p>
            <button className="voice-stop" onClick={stopRecording}><Square size={15} fill="currentColor" /> Aufnahme beenden</button>
          </div>
        )}

        {phase === "arming" && (
          <div className="voice-processing"><LoaderCircle size={28} /><p>Mikrofon wird aktiviert …</p></div>
        )}

        {phase === "processing" && (
          <div className="voice-processing"><LoaderCircle size={28} /><p>MemoHub hört das Diktat ab und erstellt einen bearbeitbaren Vorschlag.</p></div>
        )}

        {phase === "error" && (
          <div className="voice-error"><strong>Diktat nicht übernommen</strong><p>{error}</p><button onClick={onClose}>Schließen</button></div>
        )}

        {phase === "ready" && proposal && (
          <div className="voice-preview">
            <div className="voice-intent">
              <span className="voice-entry-icon" title={`Vorgeschlagenes Symbol: ${proposal.icon_key || suggestEntryIcon(proposal.title, proposal.tags)}`}>
                <EntryIcon iconKey={proposal.icon_key} title={proposal.title} size={18} />
              </span>
              <strong>{proposal.due_at ? "Aufgabe" : "Notiz"}</strong>
              <span>{Math.round(proposal.confidence * 100)} % sicher</span>
            </div>
            <label>Titel<span className="ios-zoom-guard"><input aria-label="Diktattitel" value={proposal.title} onChange={(event) => setProposal({ ...proposal, title: event.target.value })} /></span></label>
            <label>Inhalt<span className="ios-zoom-guard ios-zoom-guard-textarea"><textarea aria-label="Diktatinhalt" value={proposal.markdown} onChange={(event) => setProposal({ ...proposal, markdown: event.target.value })} /></span></label>
            <div className="voice-meta" aria-label="Datum, Uhrzeit und Priorität">
              <span className="form-section-label">Datum &amp; Uhrzeit</span>
              <div className="due-presets">
                <button onClick={() => setProposalDue(dateOffset(0))}>Heute</button>
                <button aria-label="+1 Tag" title="Einen Tag addieren" onClick={() => setProposalDue(dateOffset(1))}>
                  <span className="shortcut-long">+1 Tag</span><span className="shortcut-short" aria-hidden="true">+1</span>
                </button>
                <button aria-label="+7 Tage" title="Sieben Tage addieren" onClick={() => setProposalDue(dateOffset(7))}>
                  <span className="shortcut-long">+7 Tage</span><span className="shortcut-short" aria-hidden="true">+7</span>
                </button>
              </div>
              <div className="due-date-control">
                <span className={`date-input-shell${dueDate ? " has-value" : " is-empty"}`}>
                  <Calendar className="voice-date-icon" aria-hidden="true" size={14} />
                  <span className="ios-zoom-guard"><input aria-label="Fälligkeitsdatum des Diktats" type="date" value={dueDate} onChange={(event) => setProposalDue(event.target.value)} />
                  {!dueDate && <span className="date-empty-label" aria-hidden="true">Datum wählen</span>}</span>
                </span>
                {dueDate && <span className="voice-time-input-shell"><Clock3 className="voice-time-icon" aria-hidden="true" size={14} /><span className="ios-zoom-guard"><input className="due-time-input" aria-label="Fälligkeitszeit des Diktats" type="time" value={dueTime} onChange={(event) => setProposalDue(dueDate, event.target.value)} /></span></span>}
                {dueDate && <button className="due-clear voice-due-clear" aria-label="Fälligkeit entfernen" onClick={() => setProposalDue("")} title="Als Notiz ohne Fälligkeit übernehmen"><X size={14} /></button>}
              </div>
              {proposal.due_at && <span className="form-section-label priority-label">Priorität</span>}
              {proposal.due_at && <div className="priority-presets voice-priority" role="group" aria-label="Diktatpriorität">{(["low", "medium", "high"] as const).map((value) => <button key={value} className={`priority-${value}${proposal.priority === value ? " active" : ""}`} aria-pressed={proposal.priority === value} onClick={() => setProposal({ ...proposal, priority: value })}>{value === "low" ? "Niedrig" : value === "medium" ? "Mittel" : "Hoch"}</button>)}</div>}
            </div>
            {proposal.intent === "task" && !proposal.due_at && <p className="voice-follow-up">{proposal.follow_up_question || "Wann ist diese Aufgabe fällig?"}</p>}
            <label>Tags<span className="ios-zoom-guard"><input aria-label="Diktat-Tags" value={proposal.tags.join(", ")} onChange={(event) => setProposal({ ...proposal, tags: event.target.value.split(",").map((tag) => tag.trim()).filter(Boolean) })} /></span></label>
            {capture?.transcript && <details className="voice-transcript"><summary>Transkript anzeigen</summary><p>{capture.transcript}</p></details>}
            <footer>
              <button onClick={() => void close()}>Verwerfen</button>
              <button
                className={`voice-apply${autoSaveSeconds !== null ? " autosaving" : ""}`}
                aria-label={proposal.due_at ? "Aufgabe anlegen" : "Notiz anlegen"}
                title={autoSaveSeconds !== null ? "Automatische Speicherung läuft; jede Interaktion hält sie an" : undefined}
                onClick={() => {
                  if (autoSaveSeconds !== null) {
                    setAutoSaveSeconds(null);
                    return;
                  }
                  void apply();
                }}
              >
                {autoSaveSeconds !== null ? <><span>Autosave</span><span className="voice-autosave-count" aria-live="polite">{autoSaveSeconds}</span></> : proposal.due_at ? "Aufgabe anlegen" : "Notiz anlegen"}
              </button>
            </footer>
          </div>
        )}
      </section>
    </div>
  );
}

function SelectionToolbar({
  count,
  canArchive,
  onArchive,
  onTrash,
  onClear,
}: {
  count: number;
  canArchive: boolean;
  onArchive: () => void;
  onTrash: () => void;
  onClear: () => void;
}) {
  return (
    <div className="selection-toolbar" role="toolbar" aria-label="Ausgewählte Einträge">
      <strong>{count} ausgewählt</strong>
      {canArchive && (
        <button onClick={onArchive} title="Ausgewählte Einträge archivieren">
          <Archive size={15} /> Archivieren
        </button>
      )}
      <button className="danger" onClick={onTrash} title="Ausgewählte Einträge in den Papierkorb verschieben">
        <Trash2 size={15} /> In Papierkorb
      </button>
      <button className="clear-selection" onClick={onClear} aria-label="Auswahl aufheben" title="Auswahl aufheben (Esc)">
        <X size={16} />
      </button>
    </div>
  );
}

function EntryContextActions({
  position,
  entry,
  count,
  onOpen,
  completionPending,
  onToggleCompletion,
  onArchive,
  onTrash,
}: {
  position: EntryContextMenu;
  entry?: Entry;
  count: number;
  onOpen: () => void;
  completionPending: boolean;
  onToggleCompletion: () => void;
  onArchive: () => void;
  onTrash: () => void;
}) {
  if (!entry) return null;
  return (
    <div
      className="entry-context-menu"
      role="menu"
      aria-label="Eintragsaktionen"
      style={{
        left: Math.min(position.x, window.innerWidth - 224),
        top: Math.min(position.y, window.innerHeight - 234),
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <small>{count > 1 ? `${count} Einträge` : entry.title}</small>
      <button role="menuitem" onClick={onOpen}>Öffnen</button>
      {count <= 1 && entry.kind === "task" && !entry.archived_at && !entry.trashed_at && (
        <button role="menuitem" disabled={completionPending} onClick={onToggleCompletion}>
          <CheckCircle2 size={15} /> {flowBucket(entry) === "completed" ? "Wieder öffnen" : "Erledigen"}
        </button>
      )}
      {!entry.archived_at && (
        <button role="menuitem" onClick={onArchive}>
          <Archive size={15} /> {count > 1 ? "Auswahl archivieren" : "Archivieren"}
        </button>
      )}
      <i />
      <button className="danger" role="menuitem" onClick={onTrash}>
        <Trash2 size={15} /> {count > 1 ? "Auswahl in Papierkorb" : "In Papierkorb"}
      </button>
    </div>
  );
}

function mergeEntries(base: Entry[], changes: Entry[]) {
  const items = new Map(base.map((item) => [item.uuid, item]));
  for (const change of changes) items.set(change.uuid, change);
  return [...items.values()];
}
function routeToMemoHub() {
  window.location.assign(`/api/v2/auth/login?return_to=${encodeURIComponent(window.location.pathname + window.location.search)}`);
}
function AuthPending() {
  useEffect(() => window.turnusAuthTransition.show({
    appName: "MemoHub", checking: true, navigate: routeToMemoHub,
  }), []);
  return null;
}
function Login() {
  useEffect(() => window.turnusAuthTransition.show({
    appName: "MemoHub", navigate: routeToMemoHub,
  }), []);
  return <a hidden href="/api/v2/auth/login">Jetzt weiter</a>;
}
function Sidebar({
  page,
  session,
  navigate,
  logout,
}: {
  page: Page;
  session: Session;
  navigate: (page: Page) => void;
  logout: () => void;
}) {
  return (
    <aside className="sidebar">
      <a className="wordmark" href="/" title="MemoHub neu laden">
        <span className="brand-mark">
          <Origami size={18} />
        </span>
        MemoHub
      </a>
      <nav>
        {nav.map(({ page: key, label, icon: Icon }, index) => (
          <button
            key={key}
            className={page === key ? "active" : ""}
            title={
              key === "search"
                ? "Aktuelle Ansicht durchsuchen (⌘ K)"
                : `${label} öffnen`
            }
            onClick={() => navigate(key)}
          >
            <Icon size={19} />
            <span>{label}</span>
            {index === 4 && <i />}
          </button>
        ))}
      </nav>
      <div className="profile">
        <span>{session.display_name?.slice(0, 2).toUpperCase() || "MH"}</span>
        <div>
          <strong>{session.display_name}</strong>
          <small>Privater Bereich</small>
        </div>
        <button
          aria-label="Abmelden"
          title="Von MemoHub abmelden"
          onClick={logout}
        >
          <LogOut size={18} />
        </button>
      </div>
    </aside>
  );
}

function Flow({
  entries,
  mobileColumn,
  setMobileColumn,
  mobileSelectionMode,
  setMobileSelectionMode,
  completedOpen,
  setCompletedOpen,
  onSelect,
  selectedEntryUuids,
  onToggleSelection,
  onContextMenu,
  onMove,
  onArchive,
  onTrash,
  onOpenCalendar,
}: {
  entries: Entry[];
  mobileColumn: FlowBucket;
  setMobileColumn: (value: FlowBucket) => void;
  mobileSelectionMode: boolean;
  setMobileSelectionMode: (active: boolean) => void;
  completedOpen: boolean;
  setCompletedOpen: (open: boolean | ((current: boolean) => boolean)) => void;
  onSelect: (uuid: string) => void;
  selectedEntryUuids: Set<string>;
  onToggleSelection: (uuid: string) => void;
  onContextMenu: (uuid: string, x: number, y: number) => void;
  onMove: (entry: Entry, state: FlowBucket) => void;
  onArchive: (entry: Entry) => void;
  onTrash: (entry: Entry) => void;
  onOpenCalendar: () => void;
}) {
  const [mobileDragUuid, setMobileDragUuid] = useState<string | null>(null),
    [mobileDragTarget, setMobileDragTarget] = useState<FlowBucket | null>(null),
    [dropTarget, setDropTarget] = useState<FlowBucket | null>(null),
    columns: Array<{ state: Exclude<FlowBucket, "completed">; label: string }> = [
      { state: "today", label: "Heute" },
      { state: "upcoming", label: "Demnächst" },
      { state: "later", label: "Später" },
    ],
    boardEntries = entries.filter((item) => item.kind === "task"),
    completedEntries = boardEntries.filter(
      (item) => flowBucket(item) === "completed",
    );
  const drop = (event: ReactDragEvent<HTMLElement>, bucket: FlowBucket) => {
    event.preventDefault();
    const item = entries.find((entry) => entry.uuid === event.dataTransfer.getData("text/entry"));
    if (item) onMove(item, bucket);
    setDropTarget(null);
  };
  const mobileTargetAt = (x: number, y: number) => {
    const target = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-flow-bucket]")?.dataset.flowBucket;
    return target === "today" || target === "upcoming" || target === "later" ? target : null;
  };
  return (
    <div className="flow-layout">
      <div className="mobile-flow-controls">
        <div className={`mobile-segments ${mobileDragUuid ? "moving" : ""}`}>
          {columns.map((column) => (
            <button
              key={column.state}
              data-flow-bucket={column.state}
              className={`${mobileColumn === column.state ? "active" : ""} ${mobileDragTarget === column.state ? "drag-target" : ""}`}
              onClick={() => setMobileColumn(column.state)}
            >
              {column.label}
              <span>
                {
                  boardEntries.filter((item) => flowBucket(item) === column.state)
                    .length
                }
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`mobile-selection-toggle ${mobileSelectionMode ? "active" : ""}`}
          aria-label={mobileSelectionMode ? "Auswahl beenden" : "Mehrere Einträge auswählen"}
          aria-pressed={mobileSelectionMode}
          title={mobileSelectionMode ? "Auswahl beenden" : "Mehrere Einträge auswählen"}
          onClick={() => setMobileSelectionMode(!mobileSelectionMode)}
        >
          {mobileSelectionMode ? <X size={18} /> : <Square size={18} />}
        </button>
      </div>
      {boardEntries.some((item) => flowBucket(item) === "undated") && (
        <section aria-label="Aufgaben ohne Frist" className="collection">
          <h2>Ohne Frist</h2>
          <div className="cards">
            {boardEntries.filter((item) => flowBucket(item) === "undated").map((entry) => (
              <EntryCard key={entry.uuid} entry={entry} onSelect={onSelect}
                selected={selectedEntryUuids.has(entry.uuid)} selectionMode={selectedEntryUuids.size > 0 || mobileSelectionMode}
                onToggleSelection={onToggleSelection} onContextMenu={onContextMenu}
                onToggleCompletion={() => void onMove(entry, "completed")} onArchive={onArchive} onTrash={onTrash} />
            ))}
          </div>
        </section>
      )}
      <div className="board-area">
        <section className="board">
          {columns.map((column) => {
            const items = boardEntries
              .filter((item) => flowBucket(item) === column.state)
              .sort((a, b) =>
                column.state === "later"
                  ? a.sort_order - b.sort_order
                  : (a.due_at || "").localeCompare(b.due_at || ""),
              );
            return (
              <div
                key={column.state}
                className={`column mobile-${column.state} ${mobileColumn === column.state ? "mobile-active" : ""} ${dropTarget === column.state ? "drop-target" : ""}`}
                onDragOver={(event) => { event.preventDefault(); setDropTarget(column.state); }}
                onDragLeave={() => setDropTarget(null)}
                onDrop={(event) => drop(event, column.state)}
              >
                <header aria-hidden="true">
                  <strong>{column.label}</strong>
                  <span>{items.length}</span>
                </header>
                <div className="cards">
                  {items.map((entry) => (
                    <EntryCard
                      key={entry.uuid}
                      entry={entry}
                      onSelect={onSelect}
                      selected={selectedEntryUuids.has(entry.uuid)}
                      selectionMode={selectedEntryUuids.size > 0 || mobileSelectionMode}
                      onToggleSelection={onToggleSelection}
                      onContextMenu={onContextMenu}
                      onToggleCompletion={() => void onMove(entry, flowBucket(entry) === "completed" ? openBucket(entry) : "completed")}
                      onArchive={onArchive}
                      onTrash={onTrash}
                      onMobileDragStart={() => setMobileDragUuid(entry.uuid)}
                      onMobileDragMove={(x, y) => setMobileDragTarget(mobileTargetAt(x, y))}
                      onMobileDragEnd={(x, y) => {
                        const target = mobileTargetAt(x, y);
                        if (target) onMove(entry, target);
                        setMobileDragUuid(null);
                        setMobileDragTarget(null);
                      }}
                    />
                  ))}
                  {!items.length && (
                    <p className="column-empty">
                      {column.state === "today"
                        ? "Für heute ist nichts mehr offen."
                        : column.state === "upcoming"
                          ? "Keine kommenden Fälligkeiten."
                          : "Keine späteren Fälligkeiten."}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      </div>
      <div className="flow-rail">
        <Agenda entries={entries} onOpen={onSelect} onCalendar={onOpenCalendar} />
        <section
          className={`completed-drawer ${completedOpen ? "open" : ""} ${dropTarget === "completed" ? "drop-target" : ""}`}
          onDragOver={(event) => { event.preventDefault(); setDropTarget("completed"); }}
          onDragLeave={() => setDropTarget(null)}
          onDrop={(event) => drop(event, "completed")}
        >
          <button
            className="completed-toggle"
            aria-expanded={completedOpen}
            onClick={() => setCompletedOpen((value) => !value)}
          >
            <CheckCircle2 size={16} />
            <span>Erledigt</span>
            <b>{completedEntries.length}</b>
            {completedOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
          {completedOpen && (
            <div className="completed-list">
              {completedEntries.map((entry) => (
                <EntryCard
                  key={entry.uuid}
                  entry={entry}
                  onSelect={onSelect}
                  selected={selectedEntryUuids.has(entry.uuid)}
                  selectionMode={selectedEntryUuids.size > 0 || mobileSelectionMode}
                  onToggleSelection={onToggleSelection}
                  onContextMenu={onContextMenu}
                  onToggleCompletion={() => void onMove(entry, openBucket(entry))}
                  onArchive={onArchive}
                  onTrash={onTrash}
                  onMobileDragStart={() => setMobileDragUuid(entry.uuid)}
                  onMobileDragMove={(x, y) => setMobileDragTarget(mobileTargetAt(x, y))}
                  onMobileDragEnd={(x, y) => {
                    const target = mobileTargetAt(x, y);
                    if (target) onMove(entry, target);
                    setMobileDragUuid(null);
                    setMobileDragTarget(null);
                  }}
                />
              ))}
              {!completedEntries.length && <p>Keine erledigten Aufgaben.</p>}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function MobileCompletedStrip({
  entries,
  open,
  setOpen,
  onSelect,
  selectedEntryUuids,
  mobileSelectionMode,
  onToggleSelection,
  onContextMenu,
  onMove,
  onArchive,
  onTrash,
}: {
  entries: Entry[];
  open: boolean;
  setOpen: (open: boolean | ((current: boolean) => boolean)) => void;
  onSelect: (uuid: string) => void;
  selectedEntryUuids: Set<string>;
  mobileSelectionMode: boolean;
  onToggleSelection: (uuid: string) => void;
  onContextMenu: (uuid: string, x: number, y: number) => void;
  onMove: (entry: Entry, state: FlowBucket) => void;
  onArchive: (entry: Entry) => void;
  onTrash: (entry: Entry) => void;
}) {
  return (
    <section className={`mobile-completed-strip ${open ? "open" : ""}`} aria-label="Erledigte Aufgaben">
      <button
        type="button"
        className="mobile-completed-toggle"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <CheckCircle2 size={18} />
        <span>Erledigt</span>
        <b>{entries.length}</b>
        {open ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
      </button>
      {open && (
        <div className="mobile-completed-list">
          {entries.map((entry) => (
            <EntryCard
              key={entry.uuid}
              entry={entry}
              onSelect={onSelect}
              selected={selectedEntryUuids.has(entry.uuid)}
              selectionMode={selectedEntryUuids.size > 0 || mobileSelectionMode}
              onToggleSelection={onToggleSelection}
              onContextMenu={onContextMenu}
              onToggleCompletion={() => void onMove(entry, openBucket(entry))}
              onArchive={onArchive}
              onTrash={onTrash}
            />
          ))}
          {!entries.length && <p>Keine erledigten Aufgaben.</p>}
        </div>
      )}
    </section>
  );
}

function EntryCard({
  entry,
  onSelect,
  selected = false,
  selectionMode = false,
  onToggleSelection,
  onContextMenu,
  onToggleCompletion,
  onArchive,
  onTrash,
  onMobileDragStart,
  onMobileDragMove,
  onMobileDragEnd,
  variant = "board",
}: {
  entry: Entry;
  onSelect: (uuid: string) => void;
  selected?: boolean;
  selectionMode?: boolean;
  onToggleSelection?: (uuid: string) => void;
  onContextMenu?: (uuid: string, x: number, y: number) => void;
  onToggleCompletion?: () => void;
  onArchive?: (entry: Entry) => void;
  onTrash?: (entry: Entry) => void;
  onMobileDragStart?: () => void;
  onMobileDragMove?: (x: number, y: number) => void;
  onMobileDragEnd?: (x: number, y: number) => void;
  variant?: "board" | "calendar";
}) {
  const cardRef = useRef<HTMLElement | null>(null),
    moveTimer = useRef<number | null>(null),
    suppressClick = useRef(false),
    swipeStart = useRef<{ pointerId: number; x: number; y: number } | null>(null),
    [swipeActionsOpen, setSwipeActionsOpen] = useState(false),
    [mobileDragging, setMobileDragging] = useState(false);
  const cancelMovePress = () => {
    if (moveTimer.current !== null) window.clearTimeout(moveTimer.current);
    moveTimer.current = null;
  };
  const completed = flowBucket(entry) === "completed";
  useEffect(() => {
    if (selected || selectionMode) setSwipeActionsOpen(false);
  }, [selected, selectionMode]);
  useEffect(() => {
    if (!swipeActionsOpen) return;
    const closeOutside = (event: Event) => {
      if (event.target instanceof Node && !cardRef.current?.contains(event.target)) {
        setSwipeActionsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSwipeActionsOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside, true);
    document.addEventListener("click", closeOutside, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside, true);
      document.removeEventListener("click", closeOutside, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [swipeActionsOpen]);
  const priorityLabel =
    entry.priority === "high"
      ? "Hoch"
      : entry.priority === "medium"
        ? "Mittel"
        : entry.priority === "low"
          ? "Niedrig"
          : null;
  return (
    <article
      ref={cardRef}
      draggable={!selected}
      aria-selected={selected}
      title="Öffnen · ⌘-Klick für Mehrfachauswahl · Rechtsklick für Aktionen"
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/entry", entry.uuid);
        // Die Karte hängt unter dem Zeiger und verdeckt dadurch das Drop-Ziel nicht.
        event.dataTransfer.setDragImage(event.currentTarget, 24, -18);
      }}
      onClick={(event) => {
        if (event.button !== 0) return;
        if (suppressClick.current) {
          suppressClick.current = false;
          return;
        }
        if ((event.metaKey || selectionMode) && onToggleSelection) {
          event.preventDefault();
          onToggleSelection(entry.uuid);
          return;
        }
        if (swipeActionsOpen) {
          setSwipeActionsOpen(false);
          return;
        }
        onSelect(entry.uuid);
      }}
      onMouseDown={(event: ReactMouseEvent<HTMLElement>) => {
        if (event.button !== 2 || !onContextMenu) return;
        event.preventDefault();
        event.stopPropagation();
        onContextMenu(entry.uuid, event.clientX, event.clientY);
      }}
      onContextMenu={(event: ReactMouseEvent<HTMLElement>) => {
        if (!onContextMenu) return;
        event.preventDefault();
        event.stopPropagation();
        onContextMenu(entry.uuid, event.clientX, event.clientY);
      }}
      onPointerDown={(event: ReactPointerEvent<HTMLElement>) => {
        if (event.pointerType !== "touch") return;
        suppressClick.current = false;
        if (selected || selectionMode || (event.target as HTMLElement).closest("button")) return;
        swipeStart.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      }}
      onPointerMove={(event: ReactPointerEvent<HTMLElement>) => {
        const start = swipeStart.current;
        if (!start || start.pointerId !== event.pointerId) return;
        const deltaX = event.clientX - start.x, deltaY = event.clientY - start.y;
        if (Math.abs(deltaY) > Math.max(16, Math.abs(deltaX))) {
          swipeStart.current = null;
          return;
        }
        if (Math.abs(deltaX) > 16 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
          event.currentTarget.setPointerCapture(event.pointerId);
        }
      }}
      onPointerUp={(event: ReactPointerEvent<HTMLElement>) => {
        const start = swipeStart.current;
        swipeStart.current = null;
        if (!start || start.pointerId !== event.pointerId || selected || selectionMode) return;
        const deltaX = event.clientX - start.x;
        const deltaY = event.clientY - start.y;
        if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;
        suppressClick.current = true;
        if (deltaX < 0 && (onToggleCompletion || onArchive || onTrash)) setSwipeActionsOpen(true);
        if (deltaX > 0) setSwipeActionsOpen(false);
      }}
      onPointerCancel={() => { swipeStart.current = null; }}
      className={`entry-card ${swipeActionsOpen ? "actions-open" : ""} ${variant === "calendar" ? "calendar-entry-card" : ""} priority-${entry.priority} ${selected ? "selected multi-selected" : ""} ${flowBucket(entry) === "completed" ? "completed" : ""} ${mobileDragging ? "mobile-dragging" : ""}`}
    >
      <span
        className="drag"
        title="Gedrückt halten und zu Heute, Demnächst oder Später ziehen"
        onPointerDown={(event) => {
          if (event.pointerType !== "touch" || !onMobileDragStart || selected || selectionMode) return;
          event.stopPropagation();
          cancelMovePress();
          moveTimer.current = window.setTimeout(() => {
            suppressClick.current = true;
            setMobileDragging(true);
            onMobileDragStart();
            moveTimer.current = null;
          }, 260);
        }}
        onPointerMove={(event) => {
          if (!mobileDragging) return;
          event.preventDefault();
          onMobileDragMove?.(event.clientX, event.clientY);
        }}
        onPointerUp={(event) => {
          cancelMovePress();
          if (mobileDragging) onMobileDragEnd?.(event.clientX, event.clientY);
          setMobileDragging(false);
        }}
        onPointerCancel={() => { cancelMovePress(); setMobileDragging(false); }}
        onPointerLeave={() => { if (!mobileDragging) cancelMovePress(); }}
      >
        <GripVertical size={17} />
      </span>
      <div className="card-copy">
        <strong>{entry.title}</strong>
        <p>
          {entry.markdown
            .replace(/[#*_>\-]/g, " ")
            .trim()
            .slice(0, 100) || "Noch kein Inhalt"}
        </p>
        <footer>
          <span className="card-metadata">
            {entry.kind === "task" ? (
              <span className="due-label">
                <Calendar size={13} />
                {dueLabel(entry.due_on || entry.due_at)}
              </span>
            ) : (
              <span className="due-label">
                <FileText size={13} />
                Notiz
              </span>
            )}
            {priorityLabel && (
              <span className={`priority-badge ${entry.priority}`}>
                {priorityLabel}
              </span>
            )}
            {(entry.favorite || (entry.image_attachment_count ?? 0) > 0) && (
              <span className="card-signals">
                {entry.favorite && <Star size={14} fill="currentColor" aria-label="Favorit" />}
                {(entry.image_attachment_count ?? 0) > 0 && (
                  <Camera size={14} aria-label={`${entry.image_attachment_count} Bild${entry.image_attachment_count === 1 ? "" : "er"} angehängt`} />
                )}
              </span>
            )}
          </span>
        </footer>
      </div>
      <span className="card-icon" title={`Symbol: ${entry.icon_key || suggestEntryIcon(entry.title, entry.tags)}`}>
        <EntryIcon iconKey={entry.icon_key} title={entry.title} />
      </span>
      {swipeActionsOpen && (
        <div className="card-swipe-actions" role="group" aria-label="Eintrag verwalten" onClick={(event) => event.stopPropagation()}>
          {entry.kind === "task" && onToggleCompletion && <button type="button" onClick={() => { setSwipeActionsOpen(false); onToggleCompletion(); }}><CheckCircle2 size={15} /> {completed ? "Wieder öffnen" : "Erledigen"}</button>}
          {onArchive && <button type="button" onClick={() => { setSwipeActionsOpen(false); onArchive(entry); }}><Archive size={15} /> Archivieren</button>}
          {onTrash && <button type="button" className="danger" onClick={() => { setSwipeActionsOpen(false); onTrash(entry); }}><Trash2 size={15} /> In Papierkorb</button>}
        </div>
      )}
    </article>
  );
}
function Agenda({
  entries,
  onOpen,
  onCalendar,
}: {
  entries: Entry[];
  onOpen?: (uuid: string) => void;
  onCalendar?: () => void;
}) {
  const days = Array.from({ length: 5 }, (_, index) => {
    const value = new Date();
    value.setDate(value.getDate() - value.getDay() + 1 + index);
    return value;
  });
  return (
    <aside className="agenda">
      <header>
        <strong>Diese Woche</strong>
        <button
          aria-label="Kalender"
          title="Vollständigen Kalender anzeigen"
          onClick={onCalendar}
        >
          <CalendarClock size={18} />
        </button>
      </header>
      {days.map((day) => {
        const items = entries.filter(
          (entry) =>
            entry.kind === "task" &&
            entry.due_at &&
            flowBucket(entry) !== "completed" &&
            new Date(entry.due_at).toDateString() === day.toDateString(),
        );
        return (
          <section
            key={day.toISOString()}
            className={
              day.toDateString() === new Date().toDateString() ? "today" : ""
            }
          >
            <div>
              <b>
                {new Intl.DateTimeFormat("de-DE", { weekday: "short" }).format(
                  day,
                )}
              </b>
              <span>
                {new Intl.DateTimeFormat("de-DE", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                }).format(day)}
              </span>
              <small>{items.length ? `${items.length} Aufgaben` : ""}</small>
            </div>
            {items.map((item) => (
              <button
                className="agenda-item"
                key={item.uuid}
                onClick={() => onOpen?.(item.uuid)}
              >
                <time>{formatTime(item.due_at)}</time>
                {item.title}
              </button>
            ))}
          </section>
        );
      })}
      <button className="agenda-open" onClick={onCalendar}>
        Kalender öffnen
      </button>
    </aside>
  );
}
function MobileCalendarDock({
  entries,
  onOpen,
  onCalendar,
}: {
  entries: Entry[];
  onOpen: (uuid: string) => void;
  onCalendar: () => void;
}) {
  const [open, setOpen] = useState(false),
    upcoming = entries
      .filter((item) => item.kind === "task" && item.due_at && flowBucket(item) !== "completed")
      .sort((a, b) => (a.due_at || "").localeCompare(b.due_at || ""));
  return (
    <section className={`mobile-calendar-dock ${open ? "open" : ""}`}>
      {open && (
        <div className="mobile-calendar-panel">
          <header>
            <strong>Nächste Fälligkeiten</strong>
            <button onClick={onCalendar}>Kalender öffnen</button>
          </header>
          {upcoming.slice(0, 6).map((item) => (
            <button key={item.uuid} onClick={() => onOpen(item.uuid)}>
              <span>{dueLabel(item.due_at)}</span>
              <strong>{item.title}</strong>
            </button>
          ))}
          {!upcoming.length && <p>Keine Fälligkeiten geplant.</p>}
        </div>
      )}
      <button
        className="mobile-calendar-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <CalendarClock size={19} />
        <span>
          <strong>Kalender</strong>
          <small>
            {upcoming[0]
              ? `${dueLabel(upcoming[0].due_at)} · ${upcoming[0].title}`
              : "Keine Fälligkeiten"}
          </small>
        </span>
        {open ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
      </button>
    </section>
  );
}

function Editor({
  entry,
  intent,
  onClose,
  onActivate,
  topmost,
  windowIndex,
  onCreate,
  onSave,
  onSetCompletion,
  onSchedule,
  onPhotoCountChange,
  onArchive,
  onToast,
}: {
  entry?: Entry;
  intent: EntryIntent;
  onClose: () => void;
  onActivate: () => void;
  topmost: boolean;
  windowIndex: number;
  onCreate: (payload: api.EntryCreatePayload) => Promise<Entry>;
  onSave: (entry: Entry, changes: Partial<Entry>, requireServer?: boolean) => Promise<Entry>;
  onSetCompletion: (entry: Entry, completed: boolean) => Promise<Entry>;
  onSchedule: (entry: Entry, durationMinutes: number) => Promise<Entry>;
  onPhotoCountChange: (entryUuid: string, imageAttachmentCount: number) => void;
  onArchive?: () => void;
  onToast: (message: string) => void;
}) {
  const isNew = !entry,
    [title, setTitle] = useState(entry?.title || ""),
    [markdown, setMarkdown] = useState(entry?.markdown || ""),
    [priority, setPriority] = useState<Entry["priority"]>(
      entry?.priority || "none",
    ),
    [tags, setTags] = useState(entry?.tags.join(", ") || ""),
    [frequency, setFrequency] = useState<
      "none" | "daily" | "weekly" | "monthly"
    >(
      entry?.recurrence?.frequency === "custom"
        ? "none"
        : entry?.recurrence?.frequency || "none",
    ),
    [recurrenceMenuOpen, setRecurrenceMenuOpen] = useState(false),
    [dueAt, setDueAt] = useState(entry?.due_on || dateInput(entry?.due_at)),
    [dueTime, setDueTime] = useState(entry?.is_todo && !entry?.due_at ? "" : timeInput(entry?.due_at)),
    [calendarTransfer, setCalendarTransfer] = useState(Boolean(entry?.calendar_event_uid)),
    [calendarDuration, setCalendarDuration] = useState(entry?.calendar_duration_minutes || 30),
    [reminderMinutes, setReminderMinutes] = useState("none"),
    [reminderReady, setReminderReady] = useState(!entry),
    [reminderTouched, setReminderTouched] = useState(false),
    [reminderMenuOpen, setReminderMenuOpen] = useState(false),
    [calendarDialog, setCalendarDialog] = useState(false),
    [favorite, setFavorite] = useState(entry?.favorite || false),
    [iconKey, setIconKey] = useState(entry?.icon_key || ""),
    [iconPickerOpen, setIconPickerOpen] = useState(false),
    [preview, setPreview] = useState(false),
    [saving, setSaving] = useState(false),
    [discard, setDiscard] = useState(false),
    [duePrompt, setDuePrompt] = useState(false),
    [location, setLocation] = useState<LocationCapture | null>(null),
    [locationState, setLocationState] = useState<"idle" | "locating" | "captured" | "unavailable">(
      entry?.location ? "captured" : "idle",
    ),
    [locationExpanded, setLocationExpanded] = useState(false),
    [locationDialog, setLocationDialog] = useState(false),
    [photoMenuOpen, setPhotoMenuOpen] = useState(false),
    [photos, setPhotos] = useState<PhotoPreview[]>([]),
    [photoViewer, setPhotoViewer] = useState<PhotoPreview | null>(null),
    [position, setPosition] = useState(() => ({
      x: Math.min(windowIndex, 4) * 46,
      y: Math.min(windowIndex, 4) * 34,
    }));
  const dragStart = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    x: number;
    y: number;
  } | null>(null);
  const pendingPhotoUrls = useRef(new Set<string>());
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const photoControlRef = useRef<HTMLDivElement>(null);
  const calendarLinked = Boolean(entry?.calendar_event_id),
    isTask = Boolean(dueAt) || Boolean(entry?.is_todo) || entry?.state === "completed",
    canChangeCompletion = entry?.kind === "task" && isTask && !entry.archived_at && !entry.trashed_at,
    showTaskOptions = Boolean(dueAt) || Boolean(entry?.is_todo) || isNew,
    recordedLocation = entry?.location || location,
    recordedLocationMapUrl = recordedLocation
      ? `https://www.openstreetmap.org/?mlat=${recordedLocation.latitude}&mlon=${recordedLocation.longitude}#map=17/${recordedLocation.latitude}/${recordedLocation.longitude}`
      : null,
    normalizedTags = tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
    resolvedIconKey = iconKey || suggestEntryIcon(title, normalizedTags),
    originalFrequency =
      entry?.recurrence?.frequency === "custom"
        ? "none"
        : entry?.recurrence?.frequency || "none",
    photoUploadPending = photos.some((photo) => photo.uploading),
    dirty = isNew
      ? Boolean(
          title.trim() ||
            markdown.trim() ||
            tags.trim() ||
            favorite ||
            iconKey ||
            priority !== "none" ||
            dueAt ||
            calendarTransfer ||
            reminderMinutes !== "none" ||
            frequency !== "none" ||
            photos.length,
        )
      : title !== entry.title ||
        markdown !== entry.markdown ||
        priority !== entry.priority ||
        favorite !== entry.favorite ||
        iconKey !== (entry.icon_key || "") ||
        dueAt !== (entry.due_on || dateInput(entry.due_at)) ||
        (dueAt && dueTime !== (entry.is_todo && !entry.due_at ? "" : timeInput(entry.due_at))) ||
        normalizedTags.join("|") !== entry.tags.join("|") ||
        frequency !== originalFrequency;
  useEffect(() => {
    if (!entry) return;
    api.getReminder(entry.uuid).then(({ item }) => {
      if (item?.remind_at && entry.due_at) {
        const minutes = Math.round((new Date(entry.due_at).getTime() - new Date(item.remind_at).getTime()) / 60000);
        setReminderMinutes([5, 10, 15, 30, 60].includes(minutes) ? String(minutes) : "none");
      }
    }).catch(() => undefined).finally(() => setReminderReady(true));
  }, [entry]);
  useEffect(() => {
    if (!entry) return;
    api.listAttachments(entry.uuid).then((items) => {
      const loadedPhotos = items.filter((item) => item.media_type.startsWith("image/")).map((attachment) => ({
        key: attachment.uuid,
        filename: attachment.filename,
        attachment,
        previewUrl: api.attachmentUrl(entry.uuid, attachment.uuid),
      }));
      // Die erste Hintergrundabfrage darf ein inzwischen hinzugefügtes Bild nicht wieder entfernen.
      setPhotos((current) => {
        const loadedKeys = new Set(loadedPhotos.map((photo) => photo.key));
        return [...loadedPhotos, ...current.filter((photo) => !loadedKeys.has(photo.key))];
      });
    }).catch(() => onToast("Bilder konnten nicht geladen werden."));
  }, [entry, onToast]);
  useEffect(() => () => pendingPhotoUrls.current.forEach((url) => URL.revokeObjectURL(url)), []);
  useEffect(() => {
    const turnsIntoTask = !entry?.due_at;
    if (entry?.is_todo || !turnsIntoTask || !dueAt || !dueTime || reminderTouched || reminderMinutes !== "none") return;
    setReminderMinutes("15");
  }, [dueAt, dueTime, entry?.due_at, reminderMinutes, reminderTouched]);
  useEffect(() => {
    if (!isNew) return;
    requestLocation();
  // Der Standort gehört zum neuen Eintrag; er wird nicht bei jeder Editoränderung erneut abgefragt.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!photoMenuOpen) return;
    const dismissPhotoMenu = (event: PointerEvent) => {
      if (!photoControlRef.current?.contains(event.target as Node)) setPhotoMenuOpen(false);
    };
    document.addEventListener("pointerdown", dismissPhotoMenu);
    return () => document.removeEventListener("pointerdown", dismissPhotoMenu);
  }, [photoMenuOpen]);
  const requestClose = useCallback(
    () => (dirty ? setDiscard(true) : onClose()),
    [dirty, onClose],
  );
  useEffect(() => {
    const dismiss = () => requestClose(),
      keys = (event: KeyboardEvent) => {
        if (topmost && event.key === "Escape") {
          event.preventDefault();
          if (iconPickerOpen) {
            setIconPickerOpen(false);
            return;
          }
          requestClose();
        }
      };
    window.addEventListener("memohub-editor-dismiss", dismiss);
    window.addEventListener("keydown", keys);
    return () => {
      window.removeEventListener("memohub-editor-dismiss", dismiss);
      window.removeEventListener("keydown", keys);
    };
  }, [iconPickerOpen, requestClose, topmost]);
  useEffect(() => {
    if (isNew || !topmost) return;
    const timer = window.setTimeout(() => cancelButtonRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [isNew, topmost]);
  function requestLocation() {
    setLocationState("locating");
    void captureLocation().then(
      (value) => { setLocation(value); setLocationState("captured"); },
      () => setLocationState("unavailable"),
    );
  }
  function startDrag(event: ReactPointerEvent<HTMLElement>) {
    if (
      window.innerWidth <= 720 ||
      (event.target as HTMLElement).closest("button, input, label")
    )
      return;
    onActivate();
    dragStart.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      x: position.x,
      y: position.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function moveDrag(event: ReactPointerEvent<HTMLElement>) {
    const start = dragStart.current;
    if (!start || start.pointerId !== event.pointerId) return;
    setPosition({
      x: start.x + event.clientX - start.clientX,
      y: start.y + event.clientY - start.clientY,
    });
  }
  function stopDrag(event: ReactPointerEvent<HTMLElement>) {
    if (dragStart.current?.pointerId === event.pointerId) dragStart.current = null;
  }
  function setDue(value: string) {
    const removed = Boolean(dueAt) && !value;
    setDueAt(value);
    if (value && priority === "none") setPriority("medium");
    if (!value) {
      setFrequency("none");
      setRecurrenceMenuOpen(false);
      if (!entry?.is_todo) setPriority("none");
      setDueTime(entry?.is_todo ? "" : "09:00");
      setReminderMinutes("none");
      setReminderTouched(false);
      if (removed) onToast(entry?.is_todo ? "Fälligkeit entfernt" : "Fälligkeit entfernt – Eintrag ist jetzt eine Notiz");
    }
  }
  function shiftDue(days: number) {
    const base = dueAt ? new Date(`${dueAt}T12:00:00`) : new Date();
    base.setHours(12, 0, 0, 0);
    base.setDate(base.getDate() + days);
    setDue(localDate(base));
  }
  async function addPhotos(files: FileList | null) {
    if (!files?.length) return;
    const selected = Array.from(files).filter(isImageFile).slice(0, Math.max(0, 12 - photos.length));
    if (!selected.length) {
      setPhotoMenuOpen(false);
      onToast(photos.length >= 12 ? "Maximal 12 Bilder pro Eintrag." : "Bitte wählen Sie ein Bild aus.");
      return;
    }
    setPhotoMenuOpen(false);
    if (selected.length < files.length) onToast("Es sind maximal 12 Bilder pro Eintrag möglich.");
    if (!entry) {
      setPhotos((items) => [...items, ...selected.map((file) => {
        const previewUrl = URL.createObjectURL(file);
        pendingPhotoUrls.current.add(previewUrl);
        return { key: crypto.randomUUID(), filename: file.name, file, previewUrl };
      })]);
      return;
    }
    const pending = selected.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      pendingPhotoUrls.current.add(previewUrl);
      return { key: crypto.randomUUID(), filename: file.name, file, previewUrl, uploading: true };
    });
    setPhotos((items) => [...items, ...pending]);
    const results = await Promise.allSettled(pending.map((photo) => api.uploadAttachment(entry.uuid, photo.file!)));
    const uploaded = new Map<string, Attachment>();
    const failed: string[] = [];
    results.forEach((result, index) => {
      const photo = pending[index];
      if (result.status === "fulfilled") uploaded.set(photo.key, result.value);
      else failed.push((result.reason as Error).message || photo.filename);
      URL.revokeObjectURL(photo.previewUrl);
      pendingPhotoUrls.current.delete(photo.previewUrl);
    });
    setPhotos((items) => items.flatMap((photo) => {
      const attachment = uploaded.get(photo.key);
      if (attachment) return [{ key: attachment.uuid, filename: attachment.filename, attachment, previewUrl: api.attachmentUrl(entry.uuid, attachment.uuid) }];
      if (pending.some((candidate) => candidate.key === photo.key)) return [];
      return [photo];
    }));
    if (uploaded.size) onPhotoCountChange(entry.uuid, photos.length + uploaded.size);
    if (uploaded.size) onToast(uploaded.size === 1 ? "Bild angehängt" : `${uploaded.size} Bilder angehängt`);
    if (failed.length) onToast(`Bild konnte nicht angehängt werden: ${failed[0]}`);
  }
  async function removePhoto(photo: PhotoPreview) {
    try {
      if (entry && photo.attachment) await api.deleteAttachment(entry.uuid, photo.attachment.uuid);
      if (photo.file) {
        URL.revokeObjectURL(photo.previewUrl);
        pendingPhotoUrls.current.delete(photo.previewUrl);
      }
      setPhotos((items) => items.filter((item) => item.key !== photo.key));
      if (entry && photo.attachment) onPhotoCountChange(entry.uuid, Math.max(0, photos.length - 1));
      setPhotoViewer((current) => current?.key === photo.key ? null : current);
    } catch (error) {
      onToast((error as Error).message);
    }
  }
  function showAdjacentPhoto(direction: -1 | 1) {
    setPhotoViewer((current) => {
      if (!current) return null;
      const next = photos[photos.findIndex((photo) => photo.key === current.key) + direction];
      return next || current;
    });
  }
  async function commit(completed?: boolean) {
    if (saving || photoUploadPending) return;
    if (isNew && intent === "task" && !dueAt) {
      setDuePrompt(true);
      return;
    }
    setSaving(true);
    const recurrence =
        dueAt && dueTime && frequency !== "none"
          ? { frequency, interval: entry?.recurrence?.interval || 1 }
          : null,
      due = entry?.is_todo && !dueTime ? null : dueFromDate(dueAt, dueTime),
      dueOn = entry?.is_todo && dueAt && !dueTime ? dueAt : null,
      kind: Entry["kind"] = entry?.is_todo || due ? "task" : "note";
    try {
      let saved: Entry;
      if (isNew)
        saved = await onCreate({
          title: title.trim(),
          markdown,
          tags: normalizedTags,
          favorite,
          icon_key: iconKey || null,
          state: due && dueAt <= dateOffset(0) ? "today" : "inbox",
          focus_date: due ? dueAt : null,
          priority: due ? priority : "none",
          due_at: due,
          recurrence,
          location,
        });
      else if (completed !== undefined && !dirty)
        saved = entry;
      else
        saved = await onSave(entry, {
          title: title.trim(),
          markdown,
          tags: normalizedTags,
          favorite,
          icon_key: iconKey || null,
          priority: entry?.is_todo || due ? priority : "none",
          ...(entry?.is_todo ? { due_on: dueOn } : {}),
          due_at: due,
          focus_date: due || dueOn ? dueAt : null,
          recurrence,
        }, completed !== undefined);
      const followUpErrors: string[] = [];
      const pendingPhotos = photos.filter((photo) => photo.file);
      if (pendingPhotos.length) {
        try {
          await Promise.all(pendingPhotos.map((photo) => api.uploadAttachment(saved.uuid, photo.file!)));
          onPhotoCountChange(saved.uuid, photos.length);
        } catch (error) {
          followUpErrors.push(`Foto konnte nicht gespeichert werden: ${(error as Error).message}`);
        }
      }
      if (due && reminderReady && (!isNew || reminderMinutes !== "none" || reminderTouched)) {
        try {
          const minutes = reminderMinutes === "none" ? null : Number(reminderMinutes);
          await api.saveReminder(saved.uuid, minutes ? new Date(new Date(due).getTime() - minutes * 60000).toISOString() : null);
        } catch (error) {
          followUpErrors.push(`Erinnerung konnte nicht gespeichert werden: ${(error as Error).message}`);
        }
      }
      if (calendarTransfer) {
        if (!due) {
          followUpErrors.push("Kalendertermin konnte nicht angelegt werden: Datum und Uhrzeit fehlen.");
        } else {
          try {
            saved = await onSchedule(saved, calendarDuration);
            onToast(entry?.calendar_event_id ? "Kalendertermin aktualisiert" : "In den persönlichen Kalender übernommen");
          } catch (error) {
            if (!calendarLinked) setCalendarTransfer(false);
            followUpErrors.push(`Kalendertermin konnte nicht angelegt werden: ${(error as Error).message}`);
          }
        }
      }
      if (completed !== undefined) {
        if (followUpErrors.length) {
          onToast(`Status nicht geändert. ${followUpErrors.join(" ")}`);
          return;
        }
        await onSetCompletion(saved, completed);
      }
      onClose();
      if (followUpErrors.length) onToast(`Eintrag gespeichert. ${followUpErrors.join(" ")}`);
    } catch (error) {
      onToast((error as Error).message);
    } finally {
      setSaving(false);
    }
  }
  return (
    <section
      className={`editor editor-modal ${topmost ? "topmost" : ""} kind-${isTask ? "task" : "note"}${showTaskOptions ? " show-task-options" : ""}`}
      aria-label={
        isNew ? "Neuer Eintrag" : `Eintrag bearbeiten: ${entry.title}`
      }
      aria-modal={topmost}
      role="dialog"
      onPointerDown={onActivate}
      style={{
        zIndex: 100 + windowIndex,
        transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
      }}
    >
      <header
        className="editor-drag-handle"
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
      >
        <span className="editor-entry-icon" aria-hidden="true">
          <EntryIcon iconKey={resolvedIconKey} title={title} size={19} />
        </span>
        <span className="ios-zoom-guard editor-title-guard"><input
          aria-label="Titel"
          placeholder="Titel"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        /></span>
        <div className="editor-tools">
          <button
            className="preview-toggle"
            aria-label={preview ? "Bearbeitung öffnen" : "Vorschau öffnen"}
            title={
              preview
                ? "Zur Markdown-Bearbeitung wechseln"
                : "Markdown als Vorschau anzeigen"
            }
            onClick={() => setPreview(!preview)}
          >
            <Binoculars size={17} aria-hidden="true" />
          </button>
          <button
            aria-label="Favorit"
            title={
              favorite ? "Aus Favoriten entfernen" : "Als Favorit markieren"
            }
            className={favorite ? "active" : ""}
            onClick={() => setFavorite(!favorite)}
          >
            <Star size={17} fill={favorite ? "currentColor" : "none"} />
          </button>
          <div className="photo-control" ref={photoControlRef}>
            <button
              type="button"
              className={photoMenuOpen ? "active" : ""}
              aria-label="Bild hinzufügen"
              aria-expanded={photoMenuOpen}
              title="Bild hinzufügen"
              onClick={() => setPhotoMenuOpen((open) => !open)}
            >
              <Camera size={17} />
            </button>
            {photoMenuOpen && (
              <div className="photo-source-menu" role="menu" aria-label="Bildquelle wählen">
                <label role="menuitem">
                  <Camera size={16} /> Foto aufnehmen
                  <input type="file" accept="image/*" capture="environment" onChange={(event) => {
                    void addPhotos(event.target.files); event.currentTarget.value = "";
                  }} />
                </label>
                <label role="menuitem">
                  <ImagePlus size={16} /> Aus Fotos auswählen
                  <input type="file" accept="image/*" multiple onChange={(event) => {
                    void addPhotos(event.target.files); event.currentTarget.value = "";
                  }} />
                </label>
              </div>
            )}
          </div>
          <button
            disabled={!onArchive}
            aria-label="Archivieren"
            title={onArchive ? "Eintrag archivieren" : "Archivieren ist nach dem Speichern verfügbar"}
            onClick={onArchive}
          >
            <Archive size={17} />
          </button>
          <button
            type="button"
            className={`editor-icon-picker-button${iconPickerOpen ? " active" : ""}`}
            aria-label={`Icon der ${isTask ? "Aufgabe" : "Notiz"} ändern`}
            aria-expanded={iconPickerOpen}
            title={`Icon der ${isTask ? "Aufgabe" : "Notiz"} ändern`}
            onClick={() => setIconPickerOpen((value) => !value)}
          >
            <Palette size={17} aria-hidden="true" />
          </button>
        </div>
      </header>
      <div className="meta editor-meta">
        <div className={`editor-location-block ${locationState}${locationExpanded ? " expanded" : ""}`}>
          <button
            type="button"
            className={`editor-status-button location-indicator ${locationState}`}
            aria-label={locationState === "captured" ? "Erfassten Standort ein- oder ausklappen" : locationState === "locating" ? "Standort wird ermittelt" : locationState === "unavailable" ? "Standort erneut ermitteln" : "Standort ermitteln"}
            aria-expanded={locationState === "captured" ? locationExpanded : undefined}
            title={locationState === "captured" ? "Adresse ein- oder ausklappen" : locationState === "locating" ? "Standort wird ermittelt" : locationState === "unavailable" ? "Standort erneut ermitteln" : "Standort ermitteln"}
            onClick={() => locationState === "captured" ? setLocationExpanded((expanded) => !expanded) : requestLocation()}
          >
            <MapPin size={14} aria-hidden="true" />
            <span>{locationState === "captured" && recordedLocation ? locationSummary(recordedLocation) : locationState === "locating" ? "Standort wird ermittelt" : locationState === "unavailable" ? "Nicht verfügbar" : "Standort ermitteln"}</span>
          </button>
          {locationState === "captured" && locationExpanded && recordedLocation && (
            <div className="editor-location-expanded">
              <span>{coordinateLabel(recordedLocation)}</span>
              <div className="editor-location-actions">
                {recordedLocationMapUrl && <a href={recordedLocationMapUrl} target="_blank" rel="noreferrer"><ExternalLink size={13} /> In OpenStreetMap öffnen</a>}
                <button type="button" aria-label="Standortdetails anzeigen" onClick={() => setLocationDialog(true)}>Weitere Details</button>
              </div>
            </div>
          )}
        </div>
        <span className="ios-zoom-guard"><input className="tag-input" aria-label="Tags" placeholder="Tags" value={tags} onChange={(event) => setTags(event.target.value)} /></span>
        <div className="editor-schedule" aria-label="Datum und Uhrzeit">
          <span className="form-section-label">Datum &amp; Uhrzeit</span>
          <div className={`editor-schedule-row${dueAt ? " has-due" : " is-empty"}`}>
            <div className="due-presets">
              <button title="Fälligkeit auf heute setzen" onClick={() => setDue(dateOffset(0))}>
                Heute
              </button>
              <button aria-label="+1 Tag" title="Einen Tag zum eingetragenen Datum addieren" onClick={() => shiftDue(1)}>
                <span className="shortcut-long">+1 Tag</span><span className="shortcut-short" aria-hidden="true">+1</span>
              </button>
              <button aria-label="+7 Tage" title="Sieben Tage zum eingetragenen Datum addieren" onClick={() => shiftDue(7)}>
                <span className="shortcut-long">+7 Tage</span><span className="shortcut-short" aria-hidden="true">+7</span>
              </button>
            </div>
            <div className="due-date-control">
              <span
                className={`date-input-shell${dueAt ? " has-value" : " is-empty"}`}
                onClick={(event) => {
                  const input = event.currentTarget.querySelector<HTMLInputElement>('input[type="date"]');
                  input?.focus();
                  input?.showPicker?.();
                }}
              ><span className="ios-zoom-guard">
                <input
                  className="due-input"
                  type="date"
                  lang="de-DE"
                  aria-label="Eigenes Fälligkeitsdatum"
                  value={dueAt}
                  onChange={(event) => setDue(event.target.value)}
                />
                {!dueAt && <span className="date-empty-label" aria-hidden="true">Datum wählen</span>}
              </span><Calendar className="date-calendar-icon" size={16} aria-hidden="true" /></span>
              {dueAt && (
                <span className="ios-zoom-guard alarm-time-control"><input
                  className="due-time-input"
                  type="time"
                  lang="de-DE"
                  aria-label="Fälligkeitszeit"
                  value={dueTime}
                  onChange={(event) => setDueTime(event.target.value)}
                /><AlarmClock className="alarm-time-icon" size={16} aria-hidden="true" /></span>
              )}
              {dueAt && (
                <button
                  className="due-clear"
                  aria-label="Fälligkeit entfernen"
                  title={entry?.is_todo ? "Fälligkeit entfernen" : "Fälligkeit entfernen und als Notiz ablegen"}
                  onClick={() => setDue("")}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
        </div>
        {showTaskOptions && (
          <div className="editor-task-options">
            <div className="priority-presets" role="group" aria-label="Priorität">
              {(["low", "medium", "high"] as const).map((value) => (
                <button
                  type="button"
                  key={value}
                  className={`priority-${value}${dueAt && priority === value ? " active" : ""}`}
                  aria-pressed={Boolean(dueAt) && priority === value}
                  disabled={!dueAt || Boolean(entry?.is_todo && !dueTime)}
                  onClick={() => setPriority(value)}
                >
                  <span className="priority-label-long">{value === "low" ? "Niedrig" : value === "medium" ? "Mittel" : "Hoch"}</span>
                  <span className="priority-label-short" aria-hidden="true">{value === "low" ? "N" : value === "medium" ? "M" : "H"}</span>
                </button>
              ))}
            </div>
            <div className="reminder-control">
              <button
                type="button"
                className={`editor-status-button reminder-button${reminderMinutes !== "none" ? " active" : ""}`}
                aria-label="Erinnerung"
                aria-expanded={reminderMenuOpen}
                aria-haspopup="menu"
                disabled={!dueAt || Boolean(entry?.is_todo && !dueTime)}
                title={!dueAt ? "Erinnerung wird nach Festlegen eines Datums verfügbar" : reminderMinutes === "none" ? "Erinnerung festlegen" : `Erinnerung: ${reminderMinutes} Minuten vorher`}
                onClick={() => setReminderMenuOpen((open) => !open)}
              >
                <AlarmClock size={16} />
                <span>{reminderMinutes === "none" ? "Aus" : `${reminderMinutes} Min.`}</span>
              </button>
              {dueAt && reminderMenuOpen && (
                <div className="reminder-menu" role="menu" aria-label="Erinnerung wählen">
                  {([
                    ["none", "Aus"],
                    ["5", "5 Min."],
                    ["10", "10 Min."],
                    ["15", "15 Min."],
                    ["30", "30 Min."],
                    ["60", "60 Min."],
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      role="menuitemradio"
                      aria-checked={reminderMinutes === value}
                      onClick={() => {
                        setReminderMinutes(value);
                        setReminderTouched(true);
                        setReminderMenuOpen(false);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="recurrence-control">
              <button
                type="button"
                className={`editor-status-button recurrence-button${frequency !== "none" ? " active" : ""}`}
                aria-label="Wiederholung"
                aria-expanded={recurrenceMenuOpen}
                aria-haspopup="menu"
                disabled={!dueAt || Boolean(entry?.is_todo && !dueTime)}
                title={!dueAt ? "Wiederholung wird nach Festlegen eines Datums verfügbar" : frequency === "none" ? "Wiederholung festlegen" : `Wiederholung: ${frequency === "daily" ? "täglich" : frequency === "weekly" ? "wöchentlich" : "monatlich"}`}
                onClick={() => setRecurrenceMenuOpen((open) => !open)}
              >
                <RefreshCw size={16} />
                <span>{frequency === "none" ? "Aus" : frequency === "daily" ? "Täglich" : frequency === "weekly" ? "Wöchentlich" : "Monatlich"}</span>
              </button>
              {dueAt && recurrenceMenuOpen && (
                <div className="recurrence-menu" role="menu" aria-label="Wiederholung wählen">
                  {([
                    ["none", "Einmalig"],
                    ["daily", "Täglich"],
                    ["weekly", "Wöchentlich"],
                    ["monthly", "Monatlich"],
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      role="menuitemradio"
                      aria-checked={frequency === value}
                      onClick={() => {
                        setFrequency(value);
                        setRecurrenceMenuOpen(false);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className={`editor-status-button calendar-transfer-button${calendarLinked ? " active" : ""}`}
              aria-label="Kalenderübernahme konfigurieren"
              aria-pressed={Boolean(dueAt) && calendarLinked}
              disabled={!dueAt || Boolean(entry?.is_todo && !dueTime)}
              title={!dueAt ? "Kalenderübernahme wird nach Festlegen eines Datums verfügbar" : calendarLinked ? "Im persönlichen Kalender – Termin bearbeiten oder verschieben" : "In persönlichen Kalender übernehmen"}
              onClick={() => setCalendarDialog(true)}
            >
              <Calendar size={16} />
            </button>
          </div>
        )}
      </div>
      {preview ? (
        <div className="markdown-preview">
          <ReactMarkdown>{markdown}</ReactMarkdown>
        </div>
      ) : (
        <span className="ios-zoom-guard ios-zoom-guard-textarea"><textarea
          aria-label="Markdown-Inhalt"
          value={markdown}
          onChange={(event) => setMarkdown(event.target.value)}
          placeholder="Gedanken, Details oder Checkliste …"
        /></span>
      )}
      {photos.length > 0 && (
        <div className="editor-photo-strip" aria-label={`${photos.length} angehängte Bilder`}>
          {photos.map((photo) => (
            <div className={`editor-photo${photo.uploading ? " uploading" : ""}`} key={photo.key}>
              <button type="button" className="editor-photo-preview" aria-label={photo.uploading ? `${photo.filename} wird angehängt` : `${photo.filename} groß anzeigen`} disabled={photo.uploading} onClick={() => setPhotoViewer(photo)}>
                <img src={photo.previewUrl} alt="" />
              </button>
              {photo.uploading && <span className="editor-photo-uploading" aria-live="polite">Lädt</span>}
              <button type="button" className="editor-photo-remove" aria-label={`${photo.filename} entfernen`} disabled={photo.uploading} onClick={() => void removePhoto(photo)}>
                <X size={13} />
              </button>
            </div>
          ))}
          <button type="button" className="editor-photo-add" aria-label="Weiteres Bild hinzufügen" onClick={() => setPhotoMenuOpen(true)}>
            <Camera size={18} />
          </button>
        </div>
      )}
      <footer className="editor-footer">
        <div className={`editor-footer-actions${canChangeCompletion ? " has-completion" : ""}`}>
          <button
            ref={cancelButtonRef}
            className="discard-button"
            aria-label="Abbrechen"
            title="Abbrechen und Änderungen verwerfen (Esc)"
            onClick={requestClose}
          >
            Abbrechen
          </button>
          {entry && canChangeCompletion && (
            <button
              type="button"
              className="completion-button"
              title={flowBucket(entry) === "completed" ? "Änderungen speichern und Aufgabe wieder öffnen" : "Änderungen speichern und Aufgabe erledigen"}
              disabled={saving || photoUploadPending}
              onClick={() => void commit(flowBucket(entry) !== "completed")}
            >
              {flowBucket(entry) === "completed" ? "Wieder öffnen" : "Erledigen"}
            </button>
          )}
          <button
            className="save-button"
            aria-label="Speichern"
            disabled={saving || photoUploadPending}
            onClick={() => void commit()}
          >
            {saving ? "Speichert …" : photoUploadPending ? "Bild wird angehängt …" : "Speichern"}
          </button>
        </div>
      </footer>
      {photoViewer && createPortal(
        <div className="photo-viewer" role="dialog" aria-modal="true" aria-label={photoViewer.filename} onClick={() => setPhotoViewer(null)}>
          <img src={photoViewer.previewUrl} alt={photoViewer.filename} onClick={(event) => event.stopPropagation()} />
          {photos.length > 1 && (
            <div className="photo-viewer-navigation" aria-label="Bildergalerie">
              <button
                type="button"
                aria-label="Vorheriges Bild"
                disabled={photos.findIndex((photo) => photo.key === photoViewer.key) === 0}
                onClick={(event) => { event.stopPropagation(); showAdjacentPhoto(-1); }}
              ><ChevronLeft size={24} /></button>
              <button
                type="button"
                aria-label="Nächstes Bild"
                disabled={photos.findIndex((photo) => photo.key === photoViewer.key) === photos.length - 1}
                onClick={(event) => { event.stopPropagation(); showAdjacentPhoto(1); }}
              ><ChevronRight size={24} /></button>
            </div>
          )}
          <div className="photo-viewer-actions" onClick={(event) => event.stopPropagation()}>
            <a href={photoViewer.previewUrl} download={photoViewer.filename} aria-label={`${photoViewer.filename} herunterladen`} title="Bild herunterladen"><Download size={20} /></a>
            <button type="button" aria-label="Bildansicht schließen" onClick={() => setPhotoViewer(null)}><X size={20} /></button>
          </div>
        </div>
      , document.body)}
      {iconPickerOpen && (
        <div className="icon-picker-modal" role="dialog" aria-modal="true" aria-label={`Icon für diese ${isTask ? "Aufgabe" : "Notiz"} ändern`} onClick={() => setIconPickerOpen(false)}>
          <section className="icon-picker-panel" onClick={(event) => event.stopPropagation()}>
            <header>
              <strong>Icon für diese {isTask ? "Aufgabe" : "Notiz"}</strong>
              <button type="button" aria-label="Icon-Auswahl schließen" title="Icon-Auswahl schließen" onClick={() => setIconPickerOpen(false)}><X size={18} /></button>
            </header>
            <IconPicker
              value={iconKey || null}
              title={title}
              tags={normalizedTags}
              onChange={(value) => {
                setIconKey(value);
                setIconPickerOpen(false);
              }}
            />
          </section>
        </div>
      )}
      {discard && (
        <div
          className="editor-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-label="Änderungen verwerfen"
        >
          <div>
            <strong>Änderungen verwerfen?</strong>
            <p>Ihre nicht gespeicherten Änderungen gehen verloren.</p>
            <footer>
              <button onClick={() => setDiscard(false)}>
                Abbrechen
              </button>
              <button className="danger" onClick={onClose}>
                Verwerfen
              </button>
            </footer>
          </div>
        </div>
      )}
      {duePrompt && (
        <div
          className="editor-confirm due-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-label="Fälligkeit festlegen"
        >
          <div>
            <strong>Wann ist diese Aufgabe fällig?</strong>
            <p>
              Aufgaben aus der Aufgabenliste und dem Kalender benötigen eine Fälligkeit,
              damit sie nicht unbemerkt als Notiz abgelegt werden.
            </p>
            <div className="due-confirm-options">
              <button onClick={() => { setDue(dateOffset(0)); setDuePrompt(false); }}>Heute</button>
              <button onClick={() => { setDue(dateOffset(1)); setDuePrompt(false); }}>+1 Tag</button>
              <button onClick={() => { setDue(dateOffset(7)); setDuePrompt(false); }}>+7 Tage</button>
            </div>
            <footer>
              <button onClick={() => setDuePrompt(false)}>Abbrechen</button>
            </footer>
          </div>
        </div>
      )}
      {calendarDialog && (
        <div className="editor-confirm calendar-transfer-dialog" role="dialog" aria-modal="true" aria-label="Kalenderübernahme konfigurieren">
          <div>
            <strong>In persönlichen Kalender übernehmen</strong>
            <p>Der Eintrag wird nach dem Speichern als Termin angelegt.</p>
            <label>Dauer
              <select aria-label="Kalenderdauer" value={calendarDuration} onChange={(event) => setCalendarDuration(Number(event.target.value))}>
                <option value={15}>15 Minuten</option><option value={30}>30 Minuten</option><option value={60}>1 Stunde</option><option value={120}>2 Stunden</option>
              </select>
            </label>
            <footer>
              <button type="button" onClick={() => setCalendarDialog(false)}>Abbrechen</button>
              <button type="button" className="calendar-transfer-confirm" aria-label="Übernehmen" title="In persönlichen Kalender übernehmen" onClick={() => { setCalendarTransfer(true); setCalendarDialog(false); }}>Übernehmen</button>
            </footer>
          </div>
        </div>
      )}
      {locationDialog && <LocationStatusDialog location={recordedLocation} state={locationState} onClose={() => setLocationDialog(false)} />}
    </section>
  );
}

function LocationStatusDialog({ location, state, onClose }: { location: LocationCapture | Entry["location"] | null; state: "idle" | "locating" | "captured" | "unavailable"; onClose: () => void }) {
  const [mapExpanded, setMapExpanded] = useState(false);
  const canShowMap = Boolean(location);
  const delta = 0.003,
    bbox = location && `${location.longitude - delta}%2C${location.latitude - delta}%2C${location.longitude + delta}%2C${location.latitude + delta}`,
    mapEmbedUrl = location && `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${location.latitude}%2C${location.longitude}`,
    mapPageUrl = location && `https://www.openstreetmap.org/?mlat=${location.latitude}&mlon=${location.longitude}#map=17/${location.latitude}/${location.longitude}`;
  return <div className="location-details" role="dialog" aria-modal="true" aria-label="Standort">
    <header><strong>Standort</strong><button aria-label="Standort schließen" onClick={onClose}><X size={16} /></button></header>
    {state === "idle" && <p>Für diesen Eintrag wurde noch kein Standort erfasst.</p>}
    {state === "locating" && <p>Der Standort wird noch ermittelt.</p>}
    {state === "unavailable" && <p>Der Standort konnte nicht ermittelt werden. Prüfen Sie bitte die Standortfreigabe Ihres Browsers.</p>}
    {state === "captured" && location && <>
      <p className="location-address">{"address" in location && location.address ? location.address : "Adresse wird aufgelöst"}</p>
      <dl className="location-facts"><dt>Geokoordinaten</dt><dd>{coordinateLabel(location)}</dd><dt>Genauigkeit</dt><dd>± {Math.round(location.accuracy_meters)} m</dd><dt>Erfasst</dt><dd>{new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "medium" }).format(new Date(location.captured_at))}</dd></dl>
      {canShowMap && mapEmbedUrl && mapPageUrl && <><div className="location-map"><iframe title="Karte des erfassten Standorts" loading="lazy" src={mapEmbedUrl} /><button type="button" aria-label="Karte groß anzeigen" title="Karte groß anzeigen" onClick={() => setMapExpanded(true)}><Maximize2 size={16} /></button></div><a className="location-map-link" href={mapPageUrl} target="_blank" rel="noreferrer"><ExternalLink size={14} /> In OpenStreetMap öffnen</a></>}
    </>}
    {mapExpanded && mapEmbedUrl && mapPageUrl && <div className="location-map-expanded" role="dialog" aria-modal="true" aria-label="Große Standortkarte" onClick={() => setMapExpanded(false)}><section onClick={(event) => event.stopPropagation()}><header><strong>Standortkarte</strong><button type="button" aria-label="Große Karte schließen" onClick={() => setMapExpanded(false)}><X size={18} /></button></header><iframe title="Große Karte des erfassten Standorts" src={mapEmbedUrl} /><a className="location-map-link" href={mapPageUrl} target="_blank" rel="noreferrer"><ExternalLink size={14} /> In OpenStreetMap öffnen</a></section></div>}
  </div>;
}

function Collection({
  title,
  entries,
  onSelect,
  mobileSelectionMode,
  setMobileSelectionMode,
  selectedEntryUuids,
  onToggleSelection,
  onContextMenu,
  onMove,
  onArchive,
  onTrash,
}: {
  title: string;
  entries: Entry[];
  onSelect: (uuid: string) => void;
  mobileSelectionMode: boolean;
  setMobileSelectionMode: (active: boolean) => void;
  selectedEntryUuids: Set<string>;
  onToggleSelection: (uuid: string) => void;
  onContextMenu: (uuid: string, x: number, y: number) => void;
  onMove: (entry: Entry, state: FlowBucket) => void;
  onArchive: (entry: Entry) => void;
  onTrash: (entry: Entry) => void;
}) {
  const collectionClass =
    title === "Notizen"
      ? "collection-notes"
      : title === "Aufgaben"
        ? "collection-tasks"
        : "collection-generic";
  return (
    <section className={`collection ${collectionClass}`} aria-label={title}>
      <div className="collection-mobile-actions">
        <button
          type="button"
          className={`mobile-selection-toggle ${mobileSelectionMode ? "active" : ""}`}
          aria-label={mobileSelectionMode ? "Auswahl beenden" : "Mehrere Einträge auswählen"}
          aria-pressed={mobileSelectionMode}
          title={mobileSelectionMode ? "Auswahl beenden" : "Mehrere Einträge auswählen"}
          onClick={() => setMobileSelectionMode(!mobileSelectionMode)}
        >
          {mobileSelectionMode ? <X size={18} /> : <Square size={18} />}
        </button>
      </div>
      <div className="collection-grid">
        {entries.map((entry) => (
          <EntryCard
            entry={entry}
            onSelect={onSelect}
            selected={selectedEntryUuids.has(entry.uuid)}
            selectionMode={selectedEntryUuids.size > 0 || mobileSelectionMode}
            onToggleSelection={onToggleSelection}
            onContextMenu={onContextMenu}
            onToggleCompletion={entry.kind === "task" ? () => onMove(entry, flowBucket(entry) === "completed" ? openBucket(entry) : "completed") : undefined}
            onArchive={onArchive}
            onTrash={onTrash}
            key={entry.uuid}
          />
        ))}
        {!entries.length && <p className="empty">Keine {title.toLowerCase()} vorhanden.</p>}
      </div>
    </section>
  );
}
function CalendarView({
  entries,
  onOpen,
  onMove,
  onArchive,
  onTrash,
}: {
  entries: Entry[];
  onOpen: (uuid: string) => void;
  onMove: (entry: Entry, bucket: FlowBucket) => void;
  onArchive: (entry: Entry) => void;
  onTrash: (entry: Entry) => void;
}) {
  const [mode, setMode] = useState<"Tag" | "Woche" | "Monat">("Woche"),
    count = mode === "Tag" ? 1 : mode === "Woche" ? 5 : 30;
  const firstDay = useMemo(() => {
    const day = new Date();
    day.setHours(12, 0, 0, 0);
    if (mode === "Woche") day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
    return day;
  }, [mode]);
  return (
    <section className="calendar-view" aria-label="Kalender">
      <header>
        <div className="segments" aria-label="Kalenderansicht">
          {(["Tag", "Woche", "Monat"] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={mode === value ? "active" : ""}
              aria-pressed={mode === value}
              onClick={() => setMode(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </header>
      <div className={`week-grid mode-${mode.toLowerCase()}`}>
        {Array.from({ length: count }, (_, index) => {
          const day = new Date(firstDay);
          day.setDate(firstDay.getDate() + index);
          const scheduledEntries = entries.filter(
            (entry) =>
              entry.kind === "task" &&
              entry.due_at &&
              new Date(entry.due_at).toDateString() === day.toDateString(),
          );
          return (
            <section key={localDate(day)}>
              <h3>
                {new Intl.DateTimeFormat("de-DE", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                }).format(day)}
              </h3>
              <div className="calendar-day-entries">
                {scheduledEntries.map((entry) => (
                  <EntryCard
                    key={entry.uuid}
                    entry={entry}
                    onSelect={onOpen}
                    onToggleCompletion={() => onMove(entry, flowBucket(entry) === "completed" ? openBucket(entry) : "completed")}
                    onArchive={onArchive}
                    onTrash={onTrash}
                    variant="calendar"
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}
function Settings({ onToast, canReload, onReload }: { onToast: (message: string) => void; canReload: boolean; onReload: () => void }) {
  const [settings, setSettings] = useState<api.MemoSettings | null>(null);
  const [savedSettings, setSavedSettings] = useState("");
  const [pushReady, setPushReady] = useState(false), [pushBusy, setPushBusy] = useState(false);
  const pushSupported = supportsWebPush(), iosInstallRequired = needsIosHomeScreenInstall();
  useEffect(() => {
    api.getSettings().then((value) => {
      setSettings(value);
      setSavedSettings(JSON.stringify(value));
      if (!pushSupported || Notification.permission !== "granted") return Promise.resolve();
      return navigator.serviceWorker.ready.then(async (registration) => {
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) return;
        await api.savePushSubscription(subscription.toJSON());
        setPushReady(true);
      }).catch(() => onToast("Push-Registrierung konnte nicht erneuert werden."));
    }).catch(() => onToast("Einstellungen konnten nicht geladen werden."));
  }, [onToast, pushSupported]);
  const setPush = async (enabled: boolean) => {
    try {
      if (!settings) return;
      setPushBusy(true);
      if (!enabled) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) { await api.removePushSubscription(subscription.endpoint); await subscription.unsubscribe(); }
        setSettings(await api.saveSettings({ ...settings, push_enabled: false }));
        setPushReady(false);
        await clearAppBadge();
        onToast("Web-Push ist deaktiviert"); return;
      }
      if (iosInstallRequired) throw new Error("Bitte MemoHub zuerst zum Home-Bildschirm hinzufügen.");
      if (!pushSupported) throw new Error("Dieser Browser unterstützt Web-Push nicht.");
      if (
        Notification.permission !== "granted" &&
        (await Notification.requestPermission()) !== "granted"
      )
        throw new Error("Benachrichtigungen wurden nicht freigegeben.");
      const { public_key } = await api.webpushPublicKey(),
        registration = await navigator.serviceWorker.ready,
        existing = await registration.pushManager.getSubscription(),
        subscription = existing || await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: decodeVapidKey(public_key) });
      await api.savePushSubscription(subscription.toJSON());
      setSettings(await api.saveSettings({ ...settings, push_enabled: true }));
      setPushReady(true);
      onToast("Web-Push und App-Counter sind aktiviert");
    } catch (error) {
      onToast((error as Error).message);
    } finally {
      setPushBusy(false);
    }
  };
  if (!settings) return <section className="settings"><ReleaseStatus canReload={canReload} onReload={onReload} /><p>Ein­stellungen werden geladen …</p></section>;
  return (
    <section className="settings" aria-label="Einstellungen">
      <ReleaseStatus canReload={canReload && JSON.stringify(settings) === savedSettings} onReload={onReload} />
      <label>
        Darstellung
        <select
          value={settings.theme}
          onChange={(event) =>
            setSettings({ ...settings, theme: event.target.value as api.MemoSettings["theme"] })
          }
        >
          <option value="system">Systemeinstellung</option>
          <option value="light">Hell</option>
          <option value="dark">Dunkel</option>
        </select>
      </label>
      <label>
        Zeitzone
        <input value="Europe/Berlin" readOnly />
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={pushReady}
          disabled={pushBusy || !pushSupported || iosInstallRequired}
          onChange={(event) => void setPush(event.target.checked)}
        />{" "}
        Web-Push aktivieren
      </label>
      <p className="push-status" role="status">
        {iosInstallRequired
          ? "Auf dem iPhone zuerst zum Home-Bildschirm hinzufügen."
          : !pushSupported
            ? "Web-Push wird von diesem Browser nicht unterstützt."
            : pushReady
              ? "Aktiv: Mitteilungen und Counter am App-Symbol."
              : settings.push_enabled
                ? "Einrichtung unvollständig – bitte erneut aktivieren."
                : "Aus"}
      </p>
      {pushReady && (
        <button type="button" className="push-test" disabled={pushBusy} onClick={async () => {
          try {
            setPushBusy(true);
            await api.sendTestPush();
            onToast("Test-Mitteilung wurde gesendet");
          } catch (error) {
            onToast((error as Error).message);
          } finally {
            setPushBusy(false);
          }
        }}>Test-Mitteilung senden</button>
      )}
      <label className="check">
        <input type="checkbox" checked={settings.conceal_push_titles} onChange={(event) => setSettings({ ...settings, conceal_push_titles: event.target.checked })} /> Titel auf Sperrbildschirm
        verbergen
      </label>
      <button
        className="primary"
        onClick={async () => { const saved = await api.saveSettings(settings); setSettings(saved); setSavedSettings(JSON.stringify(saved)); document.documentElement.dataset.theme = saved.theme; onToast("Einstellungen gespeichert"); }}
      >
        Speichern
      </button>
    </section>
  );
}
function decodeVapidKey(value: string): ArrayBuffer {
  const padding = "=".repeat((4 - (value.length % 4)) % 4),
    raw = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)))
    .buffer as ArrayBuffer;
}
